import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  CustomInput,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverBody,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import classnames from "classnames";
import moment from "moment";
import * as XLSX from "xlsx-js-style";
import * as FileSaver from "file-saver";

import { AddCommaNumber, AddCommaPhoneNumber } from "../../../helpers/Utils";
import { Pagination } from "../general";
import ContentMultiForm from "./ContentMultiForm";
import { DateRangePicker } from "../../../components/common/DateRangePicker";
import {
  DASHBOARD_TOOLBAR_CONTROL_HEIGHT_STYLE,
  DASHBOARD_TOOLBAR_DANGER_BUTTON_STYLE,
  DASHBOARD_TOOLBAR_GHOST_BUTTON_STYLE,
  DASHBOARD_TOOLBAR_SELECT_STYLE,
  DASHBOARD_TOOLBAR_SOFT_ACTION_BUTTON_STYLE,
} from "../../../components/common/dashboardUx";

const fileType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const fileExtension = ".xlsx";

const CUSTOMER_TOOLBAR_PRIMARY_BUTTON_STYLE = DASHBOARD_TOOLBAR_CONTROL_HEIGHT_STYLE;
const CUSTOMER_TOOLBAR_SOFT_BUTTON_STYLE = {
  ...DASHBOARD_TOOLBAR_SOFT_ACTION_BUTTON_STYLE,
  ...DASHBOARD_TOOLBAR_CONTROL_HEIGHT_STYLE,
};
const CUSTOMER_TOOLBAR_DANGER_BUTTON_STYLE = {
  ...DASHBOARD_TOOLBAR_DANGER_BUTTON_STYLE,
  ...DASHBOARD_TOOLBAR_CONTROL_HEIGHT_STYLE,
};
const CUSTOMER_TABLE_SELECT_STYLE = DASHBOARD_TOOLBAR_SELECT_STYLE;
const CUSTOMER_ICON_BUTTON_STYLE = {
  ...DASHBOARD_TOOLBAR_GHOST_BUTTON_STYLE,
  width: 32,
  height: 32,
  minHeight: 32,
  justifyContent: "center",
  padding: 0,
};

const quickFilters = [
  { key: "all", label: "Tất cả", tone: "blue" },
  { key: "debt", label: "Có công nợ", tone: "red" },
  { key: "no_debt", label: "Không có công nợ", tone: "slate" },
  { key: "vip", label: "VIP", tone: "pink" },
  { key: "new", label: "Mới", tone: "purple" },
  { key: "sleeping", label: "Ngủ quên", tone: "gray" },
];

const CUSTOMER_DATE_DEFINED_RANGES = [
  {
    label: "Hôm nay",
    startDate: moment(),
    endDate: moment(),
  },
  {
    label: "Hôm qua",
    startDate: moment().subtract(1, "day"),
    endDate: moment().subtract(1, "day"),
  },
  {
    label: "Tuần này",
    startDate: moment().startOf("isoWeek"),
    endDate: moment().endOf("isoWeek"),
  },
  {
    label: "Tuần trước",
    startDate: moment().subtract(1, "week").startOf("isoWeek"),
    endDate: moment().subtract(1, "week").endOf("isoWeek"),
  },
  {
    label: "Tháng này",
    startDate: moment().startOf("month"),
    endDate: moment().endOf("month"),
  },
  {
    label: "Tháng trước",
    startDate: moment().subtract(1, "month").startOf("month"),
    endDate: moment().subtract(1, "month").endOf("month"),
  },
  {
    label: "Tất cả",
    startDate: moment("1900-01-01"),
    endDate: moment("1900-01-01"),
  },
];

const detailTabs = [
  { key: "info", label: "Thông tin" },
  { key: "orders", label: "Đơn hàng" },
  { key: "products", label: "Sản phẩm" },
  { key: "debt", label: "Công nợ" },
  { key: "points", label: "Tích điểm" },
  { key: "history", label: "Lịch sử" },
];

const safeArray = (value) => (Array.isArray(value) ? value : []);

const safeNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(String(value).replace(/\./g, "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const formatMoney = (value) => `${AddCommaNumber(Math.round(safeNumber(value)))} đ`;

const formatBadgeMoney = (value) => `${AddCommaNumber(Math.round(safeNumber(value)))}đ`;

const formatCount = (value) => AddCommaNumber(safeNumber(value));

const normalizeOptionBadges = (value) => {
  const items = Array.isArray(value) ? value : value ? [value] : [];
  return items
    .map((item) => {
      if (!item) return null;
      if (typeof item === "string") {
        return {
          type: "default",
          label: "",
          value: item,
          amount: 0,
        };
      }

      const label = item.label || "";
      const optionValue = item.value || item.name || item.text || "";
      const displayLabel = item.display_label || item.displayLabel || "";

      if (!label && !optionValue && !displayLabel) return null;

      return {
        ...item,
        type: item.type || "default",
        label,
        value: optionValue,
        display_label: displayLabel,
        amount: item.amount,
      };
    })
    .filter(Boolean);
};

const getProductOptionBadges = (product) => {
  const badges = normalizeOptionBadges(product && product.option_badges);
  return badges.length ? badges : normalizeOptionBadges(product && product.options);
};

const getOptionBadgeText = (badge) => {
  const typeLabel = badge.label || "";
  const value = badge.value || "";
  const shouldPrefix =
    typeLabel && value && !normalizeText(value).startsWith(normalizeText(typeLabel));
  const label =
    badge.display_label ||
    (shouldPrefix ? `${typeLabel} ${value}` : value || typeLabel);
  const amount = safeNumber(badge.amount);
  return amount > 0 ? `${label} - ${formatBadgeMoney(amount)}` : label;
};

const formatDate = (value) => {
  if (!value) return "--";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value;
};

const formatDateTime = (date, time) => {
  if (!date) return "--";
  return `${formatDate(date)}${time ? ` ${time}` : ""}`;
};

const paymentMethodLabels = {
  cash: "Tiền mặt",
  transfer: "Chuyển khoản",
  card: "Thẻ",
  credit: "Ghi nợ",
};

const formatPaymentMethods = (methods) => {
  const methodList = Array.isArray(methods) ? methods : methods ? [methods] : [];
  const labels = methodList
    .filter(Boolean)
    .map((method) => paymentMethodLabels[method] || method);
  return labels.length ? labels.join(", ") : "--";
};

const getActivityDebtDetails = (item) => {
  if (!item) {
    return {
      orderAmount: 0,
      debtAmount: 0,
      paidAmount: 0,
      isPartial: false,
      isDebt: false,
    };
  }

  const orderAmount = safeNumber(item.amount);
  let debtAmount = safeNumber(item.debt_amount);
  let paidAmount = safeNumber(item.paid_amount);

  const paymentMethods = safeArray(
    Array.isArray(item.payment_methods)
      ? item.payment_methods
      : item.payment_method
        ? [item.payment_method]
        : [],
  );
  const hasCreditMethod = paymentMethods.includes("credit");
  const hasNonCreditMethod = paymentMethods.some((m) => m && m !== "credit");
  const hasMixedMethods = hasCreditMethod && hasNonCreditMethod;

  const statusLabel = String(item.status_label || item.payment_status || "").toLowerCase();
  const title = String(item.title || "").toLowerCase();
  const isExplicitPartial =
    Boolean(item.is_partial) ||
    item.payment_status === "partial" ||
    statusLabel.includes("1 phần") ||
    title.includes("1 phần") ||
    statusLabel.includes("một phần") ||
    title.includes("một phần") ||
    hasMixedMethods;

  // Case 1: Cả 2 số tiền đều đã có và > 0
  if (debtAmount > 0 && paidAmount > 0) {
    return {
      orderAmount,
      debtAmount,
      paidAmount,
      isPartial: true,
      isDebt: true,
    };
  }

  // Case 2: debtAmount > 0 và tổng đơn > debtAmount -> phần còn lại đã trả
  if (debtAmount > 0 && orderAmount > debtAmount) {
    if (paidAmount === 0) {
      paidAmount = Math.max(0, orderAmount - debtAmount);
    }
    return {
      orderAmount,
      debtAmount,
      paidAmount,
      isPartial: true,
      isDebt: true,
    };
  }

  // Case 3: paidAmount > 0 và tổng đơn > paidAmount, có dấu hiệu công nợ/trả 1 phần
  if (paidAmount > 0 && orderAmount > paidAmount) {
    if (isExplicitPartial || hasCreditMethod || item.is_debt || item.type === "debt") {
      if (debtAmount === 0) {
        debtAmount = Math.max(0, orderAmount - paidAmount);
      }
      return {
        orderAmount,
        debtAmount,
        paidAmount,
        isPartial: true,
        isDebt: true,
      };
    }
  }

  // Case 4: Đánh dấu trả 1 phần nhưng thiếu 1 trong 2 số tiền
  if (isExplicitPartial) {
    if (debtAmount > 0 && paidAmount === 0 && orderAmount > debtAmount) {
      paidAmount = Math.max(0, orderAmount - debtAmount);
    } else if (paidAmount > 0 && debtAmount === 0 && orderAmount > paidAmount) {
      debtAmount = Math.max(0, orderAmount - paidAmount);
    } else if (orderAmount > 0 && debtAmount === 0 && paidAmount === 0) {
      debtAmount = orderAmount;
    }
    return {
      orderAmount,
      debtAmount,
      paidAmount,
      isPartial: true,
      isDebt: debtAmount > 0 || item.is_debt || item.type === "debt",
    };
  }

  const isDebt = Boolean(
    item.is_debt ||
      item.type === "debt" ||
      hasCreditMethod ||
      debtAmount > 0 ||
      statusLabel.includes("ghi nợ") ||
      title.includes("ghi nợ"),
  );

  const resolvedDebtAmount = debtAmount > 0 ? debtAmount : isDebt ? (orderAmount || debtAmount) : 0;
  const resolvedPaidAmount = paidAmount > 0 ? paidAmount : (!isDebt ? orderAmount : 0);

  return {
    orderAmount,
    debtAmount: resolvedDebtAmount,
    paidAmount: resolvedPaidAmount,
    isPartial: false,
    isDebt: isDebt && resolvedDebtAmount > 0,
  };
};

const isDebtActivity = (item) => {
  if (!item) return false;
  const details = getActivityDebtDetails(item);
  return details.isDebt || details.isPartial;
};

const isPartialDebtActivity = (item) => {
  if (!item) return false;
  return getActivityDebtDetails(item).isPartial;
};

const getActivityIcon = (item) => {
  if (isPartialDebtActivity(item)) return "simple-icon-pie-chart";
  return isDebtActivity(item) ? "simple-icon-wallet" : "simple-icon-basket";
};

const getActivityTone = (item) => {
  if (isPartialDebtActivity(item)) return "warning";
  return isDebtActivity(item) ? "debt" : "order";
};

const getBillShortCode = (billId) => {
  if (!billId) return "--";
  if (String(billId).startsWith("initial-debt")) return "Nợ ban đầu";
  return `#${String(billId).slice(0, 8)}`;
};

const getCustomerName = (customer) =>
  (customer && (customer.name || customer.phone)) || "Khách hàng";

const getInitials = (customer) => {
  const name = getCustomerName(customer);
  const words = normalizeText(name)
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "KH";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
};

const isVipCustomer = (customer) => {
  const rank = normalizeText(customer?.armorial);
  const tags = safeArray(customer?.tags).map((tag) => normalizeText(tag.label));
  return rank.includes("vip") || tags.some((tag) => tag.includes("vip"));
};

const isNewCustomer = (customer) => {
  if (!customer?.createdAt) return false;
  const createdAt = moment(customer.createdAt);
  return createdAt.isValid() && createdAt.isSame(moment(), "month");
};

const isSleepingCustomer = (customer) => {
  if (!customer?.last_order_date) return true;
  const lastOrderDate = moment(customer.last_order_date);
  return lastOrderDate.isValid() && moment().diff(lastOrderDate, "days") > 60;
};

const getUniqueTags = (dataTags) => {
  const byLabel = {};
  safeArray(dataTags).forEach((tag) => {
    if (!tag || !tag.label) return;
    const key = tag.label;
    if (!byLabel[key]) byLabel[key] = { ...tag, count: 0 };
    byLabel[key].count += 1;
  });
  return Object.values(byLabel);
};

const getRankTone = (rank) => {
  const text = normalizeText(rank);
  if (text.includes("vip") || text.includes("gold") || text.includes("vang")) return "gold";
  if (text.includes("bac") || text.includes("silver")) return "silver";
  if (text.includes("dong") || text.includes("bronze")) return "bronze";
  if (text.includes("kim cuong") || text.includes("diamond")) return "diamond";
  return "default";
};

const buildExportRows = (customers) =>
  safeArray(customers).map((customer, index) => {
    const totalBought = safeNumber(
      customer.total_products_bought || customer.product_count,
    );
    const totalDebtAmount = safeNumber(customer.debt);
    const totalSpent = safeNumber(customer.total_spent);
    let totalPaid = 0;
    let totalDebt = 0;

    if (totalDebtAmount <= 0) {
      totalPaid = totalBought;
      totalDebt = 0;
    } else if (totalSpent <= 0 || totalDebtAmount >= totalSpent) {
      totalPaid = 0;
      totalDebt = totalBought;
    } else {
      totalPaid =
        Math.round(
          ((totalSpent - totalDebtAmount) / totalSpent) * totalBought * 100,
        ) / 100;
      totalDebt = Math.max(0, Math.round((totalBought - totalPaid) * 100) / 100);
    }

    return {
      STT: index + 1,
      "Tên khách hàng": customer.name || "",
      "Số điện thoại": customer.phone || "",
      Email: customer.email || "",
      "Địa chỉ": customer.address || "",
      Hạng: customer.armorial || "",
      "Tổng chi tiêu (đ)": totalSpent,
      "Số đơn hàng": safeNumber(customer.order_count !== undefined ? customer.order_count : customer.used),
      "Tổng SL sản phẩm đã mua": totalBought,
      "SL sản phẩm đã thanh toán": totalPaid,
      "SL sản phẩm còn nợ": totalDebt,
      "Lần mua gần nhất": formatDateTime(
        customer.last_order_date,
        customer.last_order_time,
      ),
      "Công nợ (đ)": totalDebtAmount,
      "Điểm tích lũy": safeNumber(customer.point),
      Tags: safeArray(customer.tags)
        .map((tag) => tag.label)
        .join(", "),
      "Ngày tạo": formatDate(customer.createdAt),
    };
  });

const buildDailyMatrixSheetForCustomer = (customer, activity = []) => {
  const dailyMap = {};

  // 1. Prefer activity if available (contains actual bill-level paid & debt amounts)
  if (safeArray(activity).length > 0) {
    safeArray(activity).forEach((order) => {
      const d = order.date || "Không xác định";
      if (!dailyMap[d]) {
        dailyMap[d] = {
          products: {},
          products_paid: {},
          products_debt: {},
          total_count: 0,
          total_paid_count: 0,
          total_debt_count: 0,
          total_amount: 0,
          paid_amount: 0,
          debt_amount: 0,
          payment_status: "Đã thanh toán",
        };
      }
      const orderAmount = safeNumber(order.amount);
      const orderDebt = safeNumber(order.debt_amount);
      const orderPaid = safeNumber(
        order.paid_amount || Math.max(0, orderAmount - orderDebt),
      );

      dailyMap[d].total_amount += orderAmount;
      dailyMap[d].debt_amount += orderDebt;
      dailyMap[d].paid_amount += orderPaid;

      const debtRatio =
        orderAmount > 0
          ? Math.min(1, Math.max(0, orderDebt / orderAmount))
          : orderDebt > 0
            ? 1
            : 0;

      safeArray(order.products).forEach((p) => {
        const pName = p.name || `Sản phẩm #${p.product_id}`;
        const count = safeNumber(p.count);
        dailyMap[d].products[pName] =
          (dailyMap[d].products[pName] || 0) + count;
        dailyMap[d].total_count += count;

        const pDebtCount = Math.round(count * debtRatio * 100) / 100;
        const pPaidCount = Math.max(0, Math.round((count - pDebtCount) * 100) / 100);

        dailyMap[d].products_debt[pName] =
          (dailyMap[d].products_debt[pName] || 0) + pDebtCount;
        dailyMap[d].products_paid[pName] =
          (dailyMap[d].products_paid[pName] || 0) + pPaidCount;
        dailyMap[d].total_debt_count += pDebtCount;
        dailyMap[d].total_paid_count += pPaidCount;
      });

      if (dailyMap[d].debt_amount > 0 && dailyMap[d].paid_amount > 0) {
        dailyMap[d].payment_status = "Thanh toán 1 phần";
      } else if (dailyMap[d].debt_amount > 0) {
        dailyMap[d].payment_status = "Ghi nợ";
      } else {
        dailyMap[d].payment_status = "Đã thanh toán";
      }
    });
  } else {
    // 2. Check daily_matrix from customer
    const dailyList = safeArray(customer && customer.daily_matrix);
    if (dailyList.length) {
      dailyList.forEach((day) => {
        const d = day.date || "Không xác định";
        if (!dailyMap[d]) {
          dailyMap[d] = {
            products: {},
            products_paid: {},
            products_debt: {},
            total_count: 0,
            total_paid_count: 0,
            total_debt_count: 0,
            total_amount: 0,
            paid_amount: 0,
            debt_amount: 0,
            payment_status: day.payment_status || "Đã thanh toán",
          };
        }
        const dayTotalAmt = safeNumber(day.total_amount);
        const dayDebtAmt = safeNumber(day.debt_amount);
        const dayPaidAmt = safeNumber(
          day.paid_amount || Math.max(0, dayTotalAmt - dayDebtAmt),
        );

        dailyMap[d].total_amount += dayTotalAmt;
        dailyMap[d].debt_amount += dayDebtAmt;
        dailyMap[d].paid_amount += dayPaidAmt;

        dailyMap[d].total_count += safeNumber(day.total_count);
        dailyMap[d].total_paid_count += safeNumber(day.total_paid_count);
        dailyMap[d].total_debt_count += safeNumber(day.total_debt_count);

        Object.entries(day.products || {}).forEach(([pName, qty]) => {
          dailyMap[d].products[pName] =
            (dailyMap[d].products[pName] || 0) + safeNumber(qty);
        });
        Object.entries(day.products_paid || {}).forEach(([pName, qty]) => {
          dailyMap[d].products_paid[pName] =
            (dailyMap[d].products_paid[pName] || 0) + safeNumber(qty);
        });
        Object.entries(day.products_debt || {}).forEach(([pName, qty]) => {
          dailyMap[d].products_debt[pName] =
            (dailyMap[d].products_debt[pName] || 0) + safeNumber(qty);
        });

        if (dailyMap[d].debt_amount > 0 && dailyMap[d].paid_amount > 0) {
          dailyMap[d].payment_status = "Thanh toán 1 phần";
        } else if (dailyMap[d].debt_amount > 0) {
          dailyMap[d].payment_status = "Ghi nợ";
        } else {
          dailyMap[d].payment_status = "Đã thanh toán";
        }
      });
    }
  }

  // 3. Fallback if no order items but customer has debt or used count
  if (!Object.keys(dailyMap).length) {
    const debt = safeNumber(customer && customer.debt);
    const spent = safeNumber(customer && customer.total_spent);
    const used = safeNumber(
      customer &&
        (customer.order_count !== undefined
          ? customer.order_count
          : customer.used),
    );
    const createdDate = formatDate(customer && customer.createdAt);
    if (debt > 0 || used > 0 || spent > 0) {
      const pName = debt > 0 ? "Công nợ tồn / Đơn hàng đầu kỳ" : "Đơn hàng đã mua";
      const totalAmount = spent || debt;
      const paidAmt = Math.max(0, totalAmount - debt);
      const isPartial = debt > 0 && paidAmt > 0;
      const statusText = isPartial
        ? "Thanh toán 1 phần"
        : debt > 0
          ? "Ghi nợ"
          : "Đã thanh toán";
      const totalCount = used || 1;
      let paidCount = 0;
      let debtCount = 0;
      if (debt <= 0) {
        paidCount = totalCount;
        debtCount = 0;
      } else if (paidAmt <= 0) {
        paidCount = 0;
        debtCount = totalCount;
      } else {
        paidCount =
          totalAmount > 0
            ? Math.round((paidAmt / totalAmount) * totalCount * 100) / 100
            : 0;
        debtCount = Math.max(0, Math.round((totalCount - paidCount) * 100) / 100);
      }

      return [
        {
          "Ngày/Tháng": createdDate,
          [pName]: totalCount,
          "Tổng SL mua": totalCount,
          "SL đã thanh toán": paidCount,
          "SL còn nợ": debtCount,
          "Tổng tiền hàng (đ)": totalAmount,
          "Tiền đã trả (đ)": paidAmt,
          "Tiền nợ (đ)": debt,
          "Trạng thái thanh toán": statusText,
        },
        {
          "Ngày/Tháng": "Tổng cộng",
          [pName]: totalCount,
          "Tổng SL mua": totalCount,
          "SL đã thanh toán": paidCount,
          "SL còn nợ": debtCount,
          "Tổng tiền hàng (đ)": totalAmount,
          "Tiền đã trả (đ)": paidAmt,
          "Tiền nợ (đ)": debt,
          "Trạng thái thanh toán": statusText,
        },
      ];
    }
    return [
      {
        "Ngày/Tháng": "--",
        "Chưa có phát sinh hàng": "--",
        "Tổng SL mua": 0,
        "SL đã thanh toán": 0,
        "SL còn nợ": 0,
        "Tổng tiền hàng (đ)": 0,
        "Tiền đã trả (đ)": 0,
        "Tiền nợ (đ)": 0,
        "Trạng thái thanh toán": "--",
      },
    ];
  }

  // 4. Collect all unique product names across all dates
  const productNamesSet = new Set();
  Object.values(dailyMap).forEach((dayData) => {
    Object.keys(dayData.products || {}).forEach((name) => productNamesSet.add(name));
  });
  const productNames = Array.from(productNamesSet);

  // 5. Sort dates chronologically (oldest to newest)
  const sortedDates = Object.keys(dailyMap).sort((a, b) => {
    const timeA = new Date(a).getTime() || 0;
    const timeB = new Date(b).getTime() || 0;
    return timeA - timeB;
  });

  const columnTotals = {};
  productNames.forEach((name) => {
    columnTotals[name] = 0;
  });
  let grandTotalBought = 0;
  let grandTotalPaid = 0;
  let grandTotalDebt = 0;
  let grandTotalAmount = 0;
  let grandPaidAmount = 0;
  let grandDebtAmount = 0;

  const rows = [];
  sortedDates.forEach((dateStr) => {
    const dayData = dailyMap[dateStr] || {};
    const prods = dayData.products || {};
    const formattedDate = formatDate(dateStr);
    const row = {
      "Ngày/Tháng": formattedDate,
    };

    let dayCount = 0;
    productNames.forEach((name) => {
      const qty = safeNumber(prods[name]);
      row[name] = qty > 0 ? qty : "";
      if (qty > 0) {
        columnTotals[name] += qty;
        dayCount += qty;
      }
    });

    const totalCount = dayData.total_count || dayCount;
    const totalAmount = safeNumber(dayData.total_amount);
    const debtAmount = safeNumber(dayData.debt_amount);
    const paidAmount =
      safeNumber(dayData.paid_amount) || Math.max(0, totalAmount - debtAmount);

    let totalPaid = 0;
    let totalDebt = 0;

    if (debtAmount <= 0) {
      totalPaid = totalCount;
      totalDebt = 0;
    } else if (paidAmount <= 0) {
      totalPaid = 0;
      totalDebt = totalCount;
    } else {
      // Partial debt: calculate totalPaid from ratio, and totalDebt = totalCount - totalPaid
      if (totalAmount > 0) {
        totalPaid =
          Math.round((paidAmount / totalAmount) * totalCount * 100) / 100;
      } else {
        totalPaid = safeNumber(dayData.total_paid_count);
      }
      totalDebt = Math.max(0, Math.round((totalCount - totalPaid) * 100) / 100);
    }

    grandTotalBought += totalCount;
    grandTotalPaid += totalPaid;
    grandTotalDebt += totalDebt;
    grandTotalAmount += totalAmount;
    grandPaidAmount += paidAmount;
    grandDebtAmount += debtAmount;

    row["Tổng SL mua"] = totalCount;
    row["SL đã thanh toán"] = totalPaid;
    row["SL còn nợ"] = totalDebt;
    row["Tổng tiền hàng (đ)"] = totalAmount;
    row["Tiền đã trả (đ)"] = paidAmount;
    row["Tiền nợ (đ)"] = debtAmount;
    row["Trạng thái thanh toán"] =
      dayData.payment_status ||
      (debtAmount > 0
        ? paidAmount > 0
          ? "Thanh toán 1 phần"
          : "Ghi nợ"
        : "Đã thanh toán");

    rows.push(row);
  });

  // 6. Summary row at the bottom ("Tổng cộng")
  const totalRow = {
    "Ngày/Tháng": "Tổng cộng",
  };
  productNames.forEach((name) => {
    totalRow[name] = columnTotals[name] || 0;
  });
  totalRow["Tổng SL mua"] = grandTotalBought;
  totalRow["SL đã thanh toán"] = grandTotalPaid;
  totalRow["SL còn nợ"] = Math.max(
    0,
    Math.round((grandTotalBought - grandTotalPaid) * 100) / 100,
  );
  totalRow["Tổng tiền hàng (đ)"] = grandTotalAmount;
  totalRow["Tiền đã trả (đ)"] = grandPaidAmount;
  totalRow["Tiền nợ (đ)"] = grandDebtAmount;
  totalRow["Trạng thái thanh toán"] =
    grandDebtAmount > 0
      ? grandPaidAmount > 0
        ? "Còn nợ một phần"
        : "Ghi nợ"
      : "Đã thanh toán đủ";

  rows.push(totalRow);

  return rows;
};

const buildDebtMatrixSheetForCustomer = (customer, activity = []) => {
  const dailyMap = {};

  if (safeArray(activity).length > 0) {
    safeArray(activity).forEach((order) => {
      const orderDebt = safeNumber(order.debt_amount);
      if (orderDebt > 0) {
        const d = order.date || "Không xác định";
        if (!dailyMap[d]) {
          dailyMap[d] = {
            products_debt: {},
            total_debt_count: 0,
            debt_amount: 0,
            paid_amount: 0,
          };
        }
        const orderAmount = safeNumber(order.amount);
        const orderPaid = safeNumber(
          order.paid_amount || Math.max(0, orderAmount - orderDebt),
        );
        dailyMap[d].debt_amount += orderDebt;
        dailyMap[d].paid_amount += orderPaid;

        const debtRatio =
          orderAmount > 0
            ? Math.min(1, Math.max(0, orderDebt / orderAmount))
            : 1;

        safeArray(order.products).forEach((p) => {
          const pName = p.name || `Sản phẩm #${p.product_id}`;
          const count = safeNumber(p.count);
          const pDebtCount = Math.round(count * debtRatio * 100) / 100;
          dailyMap[d].products_debt[pName] =
            (dailyMap[d].products_debt[pName] || 0) + pDebtCount;
          dailyMap[d].total_debt_count += pDebtCount;
        });
      }
    });
  } else {
    const dailyList = safeArray(customer && customer.daily_matrix);
    if (dailyList.length) {
      dailyList.forEach((day) => {
        const debtAmt = safeNumber(day.debt_amount);
        const debtCount = safeNumber(day.total_debt_count);
        const hasDebtProds = Object.keys(day.products_debt || {}).length > 0;
        if (debtAmt > 0 || debtCount > 0 || hasDebtProds) {
          const d = day.date || "Không xác định";
          dailyMap[d] = {
            products_debt: { ...(day.products_debt || {}) },
            total_debt_count: debtCount,
            debt_amount: debtAmt,
            paid_amount: safeNumber(day.paid_amount),
          };
        }
      });
    }
  }

  const customerDebt = safeNumber(customer && customer.debt);
  if (!Object.keys(dailyMap).length) {
    if (customerDebt > 0) {
      return [
        {
          "Ngày/Tháng": formatDate(customer && customer.createdAt),
          "Khoản nợ tồn": 1,
          "Tổng SL nợ": 1,
          "Tiền nợ (đ)": customerDebt,
          "Ghi chú": "Công nợ đầu kỳ / chưa thanh toán",
        },
        {
          "Ngày/Tháng": "Tổng cộng",
          "Khoản nợ tồn": 1,
          "Tổng SL nợ": 1,
          "Tiền nợ (đ)": customerDebt,
          "Ghi chú": "Tổng công nợ hiện tại",
        },
      ];
    }
    return [
      {
        "Ngày/Tháng": "Tất cả",
        "Trạng thái": "Khách hàng không có công nợ tồn đọng (Đã thanh toán đủ)",
        "Tổng SL nợ": 0,
        "Tiền nợ (đ)": 0,
      },
    ];
  }

  const productNamesSet = new Set();
  Object.values(dailyMap).forEach((dayData) => {
    Object.keys(dayData.products_debt || {}).forEach((name) =>
      productNamesSet.add(name),
    );
  });
  const productNames = Array.from(productNamesSet);

  const sortedDates = Object.keys(dailyMap).sort((a, b) => {
    const timeA = new Date(a).getTime() || 0;
    const timeB = new Date(b).getTime() || 0;
    return timeA - timeB;
  });

  const columnTotals = {};
  productNames.forEach((name) => {
    columnTotals[name] = 0;
  });
  let grandTotalDebtCount = 0;
  let grandTotalDebtAmt = 0;

  const rows = [];
  sortedDates.forEach((dateStr) => {
    const dayData = dailyMap[dateStr] || {};
    const prods = dayData.products_debt || {};
    const formattedDate = formatDate(dateStr);
    const row = {
      "Ngày/Tháng": formattedDate,
    };

    let dayCount = 0;
    productNames.forEach((name) => {
      const qty = safeNumber(prods[name]);
      row[name] = qty > 0 ? qty : "";
      if (qty > 0) {
        columnTotals[name] += qty;
        dayCount += qty;
      }
    });

    const totalCount = dayData.total_debt_count || dayCount;
    const debtAmt = safeNumber(dayData.debt_amount);
    const paidAmt = safeNumber(dayData.paid_amount);

    grandTotalDebtCount += totalCount;
    grandTotalDebtAmt += debtAmt;

    row["Tổng SL nợ"] = totalCount > 0 ? totalCount : "";
    row["Tiền nợ (đ)"] = debtAmt;
    row["Ghi chú"] =
      paidAmt > 0 ? "Thanh toán 1 phần (còn nợ)" : "Chưa thanh toán";
    rows.push(row);
  });

  const totalRow = {
    "Ngày/Tháng": "Tổng cộng",
  };
  productNames.forEach((name) => {
    totalRow[name] = columnTotals[name] || 0;
  });
  totalRow["Tổng SL nợ"] = grandTotalDebtCount;
  totalRow["Tiền nợ (đ)"] = grandTotalDebtAmt;
  totalRow["Ghi chú"] = "Tổng nợ chưa thanh toán";
  rows.push(totalRow);

  return rows;
};

const buildTransactionDetailSheetForCustomer = (customer, activity = []) => {
  const rows = [];
  let index = 1;

  safeArray(activity).forEach((order) => {
    const orderAmount = safeNumber(order.amount);
    const orderDebt = safeNumber(order.debt_amount);
    const orderPaid = safeNumber(
      order.paid_amount || Math.max(0, orderAmount - orderDebt),
    );
    const paymentStr = formatPaymentMethods(order.payment_methods);
    const prods = safeArray(order.products);

    const isPartial = orderDebt > 0 && orderPaid > 0;
    const isFullDebt = orderDebt > 0 && orderPaid === 0;
    const statusLabel = isPartial
      ? "Thanh toán 1 phần (Còn nợ)"
      : isFullDebt
        ? "Ghi nợ (Chưa TT)"
        : "Đã thanh toán";

    const debtRatio =
      orderAmount > 0
        ? Math.min(1, Math.max(0, orderDebt / orderAmount))
        : orderDebt > 0
          ? 1
          : 0;

    if (prods.length) {
      prods.forEach((p) => {
        const count = safeNumber(p.count);
        const itemAmount = safeNumber(p.amount);
        const itemDebt = Math.round(itemAmount * debtRatio);
        const itemPaid = Math.max(0, itemAmount - itemDebt);

        rows.push({
          STT: index++,
          Ngày: formatDate(order.date),
          Giờ: order.time || "--",
          "Mã đơn hàng": getBillShortCode(order.bill_id),
          "Tên sản phẩm": p.name || `Sản phẩm #${p.product_id}`,
          "Đơn vị tính": p.sale_unit || "--",
          "Số lượng": count,
          "Đơn giá (đ)": count > 0 ? Math.round(itemAmount / count) : itemAmount,
          "Thành tiền (đ)": itemAmount,
          "Hình thức TT": paymentStr,
          "Tiền đã trả (đ)": itemPaid,
          "Tiền ghi nợ (đ)": itemDebt,
          "Trạng thái thanh toán": statusLabel,
        });
      });
    } else {
      rows.push({
        STT: index++,
        Ngày: formatDate(order.date),
        Giờ: order.time || "--",
        "Mã đơn hàng": getBillShortCode(order.bill_id),
        "Tên sản phẩm": "Đơn hàng tổng hợp",
        "Đơn vị tính": "đơn",
        "Số lượng": safeNumber(order.item_count) || 1,
        "Đơn giá (đ)": orderAmount,
        "Thành tiền (đ)": orderAmount,
        "Hình thức TT": paymentStr,
        "Tiền đã trả (đ)": orderPaid,
        "Tiền ghi nợ (đ)": orderDebt,
        "Trạng thái thanh toán": statusLabel,
      });
    }
  });

  if (!rows.length) {
    const debt = safeNumber(customer && customer.debt);
    const spent = safeNumber(customer && customer.total_spent);
    const paidAmt = Math.max(0, spent - debt);
    return [
      {
        STT: 1,
        Ngày: formatDate(customer && customer.createdAt),
        Giờ: "--",
        "Mã đơn hàng": "--",
        "Tên sản phẩm": "Dữ liệu tổng hợp",
        "Đơn vị tính": "--",
        "Số lượng":
          safeNumber(
            customer &&
              (customer.order_count !== undefined
                ? customer.order_count
                : customer.used),
          ) || 1,
        "Đơn giá (đ)": spent,
        "Thành tiền (đ)": spent,
        "Hình thức TT": debt > 0 ? "Ghi nợ" : "Tiền mặt/CK",
        "Tiền đã trả (đ)": paidAmt,
        "Tiền ghi nợ (đ)": debt,
        "Trạng thái thanh toán":
          debt > 0 ? (paidAmt > 0 ? "Thanh toán 1 phần" : "Ghi nợ") : "Đã thanh toán",
      },
    ];
  }

  return rows;
};

const applyExcelWorksheetStyles = (ws, options = {}) => {
  if (!ws || !ws["!ref"]) return;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  const { productNames = [] } = options;
  const productSet = new Set(productNames);

  // Border presets
  const thinBorder = {
    top: { style: "thin", color: { rgb: "E2E8F0" } },
    bottom: { style: "thin", color: { rgb: "E2E8F0" } },
    left: { style: "thin", color: { rgb: "E2E8F0" } },
    right: { style: "thin", color: { rgb: "E2E8F0" } },
  };
  const headerBorder = {
    top: { style: "thin", color: { rgb: "CBD5E1" } },
    bottom: { style: "medium", color: { rgb: "64748B" } },
    left: { style: "thin", color: { rgb: "CBD5E1" } },
    right: { style: "thin", color: { rgb: "CBD5E1" } },
  };
  const totalRowBorder = {
    top: { style: "medium", color: { rgb: "475569" } },
    bottom: { style: "double", color: { rgb: "0F172A" } },
    left: { style: "thin", color: { rgb: "CBD5E1" } },
    right: { style: "thin", color: { rgb: "CBD5E1" } },
  };

  // Extract header names from row 0
  const headers = [];
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const addr = XLSX.utils.encode_cell({ r: 0, c: C });
    headers.push(ws[addr] ? String(ws[addr].v || "") : "");
  }

  for (let R = range.s.r; R <= range.e.r; ++R) {
    const isHeader = R === 0;
    const isLastRow = R === range.e.r;
    const firstCell = ws[XLSX.utils.encode_cell({ r: R, c: 0 })];
    const isTotalRow =
      !isHeader &&
      (isLastRow ||
        (firstCell &&
          String(firstCell.v || "")
            .toLowerCase()
            .includes("tổng cộng")));

    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      let cell = ws[addr];
      if (!cell) {
        cell = { t: "s", v: "" };
        ws[addr] = cell;
      }

      const headerName = headers[C] || "";
      const val = cell.v !== null && cell.v !== undefined ? cell.v : "";
      const numVal = Number(val);
      const isNum = !isNaN(numVal) && typeof val === "number";
      const isProductCol =
        productSet.has(headerName) ||
        (!isTotalRow &&
          !isHeader &&
          headerName !== "Ngày/Tháng" &&
          headerName !== "STT" &&
          headerName !== "Ngày" &&
          headerName !== "Giờ" &&
          headerName !== "Mã đơn hàng" &&
          headerName !== "Tổng SL mua" &&
          headerName !== "SL đã thanh toán" &&
          headerName !== "SL còn nợ" &&
          headerName !== "Tổng tiền hàng (đ)" &&
          headerName !== "Tiền đã trả (đ)" &&
          headerName !== "Tiền nợ (đ)" &&
          headerName !== "Trạng thái thanh toán" &&
          headerName !== "Ghi chú" &&
          headerName !== "Đơn vị tính" &&
          headerName !== "Đơn giá (đ)" &&
          headerName !== "Thành tiền (đ)" &&
          headerName !== "Hình thức TT" &&
          headerName !== "Tên khách hàng" &&
          headerName !== "Số điện thoại" &&
          headerName !== "Email" &&
          headerName !== "Địa chỉ" &&
          headerName !== "Hạng" &&
          headerName !== "Tổng chi tiêu (đ)" &&
          headerName !== "Số đơn hàng" &&
          headerName !== "Tổng SL sản phẩm đã mua" &&
          headerName !== "SL sản phẩm đã thanh toán" &&
          headerName !== "SL sản phẩm còn nợ" &&
          headerName !== "Lần mua gần nhất" &&
          headerName !== "Công nợ (đ)" &&
          headerName !== "Điểm tích lũy" &&
          headerName !== "Tags" &&
          headerName !== "Ngày tạo");

      const isDateCol =
        headerName === "Ngày/Tháng" ||
        headerName === "Ngày" ||
        headerName === "Giờ" ||
        headerName === "Lần mua gần nhất" ||
        headerName === "Ngày tạo";
      const isIdCol =
        headerName === "STT" ||
        headerName === "Mã đơn hàng" ||
        headerName === "Số điện thoại";
      const isTotalQtyCol =
        headerName === "Tổng SL mua" ||
        headerName === "Số lượng" ||
        headerName === "Tổng SL sản phẩm đã mua";
      const isPaidQtyCol =
        headerName === "SL đã thanh toán" ||
        headerName === "SL đã trả" ||
        headerName === "SL sản phẩm đã thanh toán";
      const isDebtQtyCol =
        headerName === "SL còn nợ" ||
        headerName === "Tổng SL nợ" ||
        headerName === "Khoản nợ tồn" ||
        headerName === "SL sản phẩm còn nợ";
      const isTotalMoneyCol =
        headerName === "Tổng tiền hàng (đ)" ||
        headerName === "Tổng chi tiêu (đ)" ||
        headerName === "Thành tiền (đ)" ||
        headerName === "Đơn giá (đ)";
      const isPaidMoneyCol =
        headerName === "Tiền đã trả (đ)" || headerName === "Số tiền đã trả (đ)";
      const isDebtMoneyCol =
        headerName === "Tiền nợ (đ)" ||
        headerName === "Tiền ghi nợ (đ)" ||
        headerName === "Công nợ (đ)";
      const isStatusCol =
        headerName === "Trạng thái thanh toán" ||
        headerName === "Trạng thái" ||
        headerName === "Hình thức TT" ||
        headerName === "Ghi chú";

      if (isHeader) {
        let fillRgb = "F1F5F9"; // Light slate default
        let fontColor = "1E293B";

        if (isProductCol) {
          fillRgb = "FEF08A"; // Warm light yellow header for product columns
          fontColor = "854D0E";
        } else if (isTotalQtyCol) {
          fillRgb = "E0F2FE"; // Soft sky blue
          fontColor = "0369A1";
        } else if (isPaidQtyCol) {
          fillRgb = "DCFCE7"; // Soft mint green
          fontColor = "15803D";
        } else if (isDebtQtyCol) {
          fillRgb = "FFE4E6"; // Soft rose
          fontColor = "BE123C";
        } else if (isTotalMoneyCol) {
          fillRgb = "DBEAFE"; // Soft light blue
          fontColor = "1D4ED8";
        } else if (isPaidMoneyCol) {
          fillRgb = "D1FAE5"; // Soft emerald
          fontColor = "047857";
        } else if (isDebtMoneyCol) {
          fillRgb = "FEE2E2"; // Soft rose red
          fontColor = "B91C1C";
        } else if (isStatusCol) {
          fillRgb = "EDE9FE"; // Soft violet
          fontColor = "6D28D9";
        }

        cell.s = {
          fill: { fgColor: { rgb: fillRgb } },
          font: { bold: true, color: { rgb: fontColor }, sz: 11, name: "Calibri" },
          alignment: {
            horizontal:
              isTotalMoneyCol || isPaidMoneyCol || isDebtMoneyCol
                ? "right"
                : "center",
            vertical: "center",
            wrapText: true,
          },
          border: headerBorder,
        };
      } else if (isTotalRow) {
        cell.s = {
          fill: { fgColor: { rgb: "FEF08A" } }, // Highlight summary gold
          font: { bold: true, color: { rgb: "0F172A" }, sz: 11, name: "Calibri" },
          alignment: {
            horizontal:
              isTotalMoneyCol || isPaidMoneyCol || isDebtMoneyCol
                ? "right"
                : "center",
            vertical: "center",
          },
          border: totalRowBorder,
          numFmt:
            isTotalMoneyCol || isPaidMoneyCol || isDebtMoneyCol
              ? "#,##0"
              : undefined,
        };
      } else {
        const isEven = R % 2 === 0;
        let cellFill = isEven ? "F8FAFC" : "FFFFFF";
        let fontColor = "0F172A";
        let isBold = false;
        let horizAlign = "left";
        let numFmt = undefined;

        if (isDateCol || isIdCol) {
          horizAlign = "center";
          if (isIdCol) fontColor = "64748B";
        } else if (isProductCol) {
          horizAlign = "center";
          cellFill =
            isNum && numVal > 0 ? "FEFCE8" : isEven ? "F8FAFC" : "FFFFFF";
          if (isNum && numVal > 0) {
            fontColor = "854D0E";
            isBold = true;
          }
        } else if (isTotalQtyCol) {
          horizAlign = "center";
          cellFill = isEven ? "F0F9FF" : "F8FAFC";
          isBold = true;
          fontColor = "0369A1";
        } else if (isPaidQtyCol) {
          horizAlign = "center";
          cellFill = isEven ? "F0FDF4" : "F8FAFC";
          if (isNum && numVal > 0) fontColor = "15803D";
        } else if (isDebtQtyCol) {
          horizAlign = "center";
          if (isNum && numVal > 0) {
            cellFill = "FFF1F2";
            fontColor = "BE123C";
            isBold = true;
          }
        } else if (isTotalMoneyCol) {
          horizAlign = "right";
          numFmt = "#,##0";
          fontColor = "1E293B";
        } else if (isPaidMoneyCol) {
          horizAlign = "right";
          numFmt = "#,##0";
          if (isNum && numVal > 0) fontColor = "15803D";
        } else if (isDebtMoneyCol) {
          horizAlign = "right";
          numFmt = "#,##0";
          if (isNum && numVal > 0) {
            cellFill = "FFF1F2";
            fontColor = "BE123C";
            isBold = true;
          }
        } else if (isStatusCol) {
          horizAlign = "center";
          const strVal = String(val);
          if (strVal.includes("Đã thanh toán")) {
            cellFill = "DCFCE7";
            fontColor = "166534";
            isBold = true;
          } else if (
            strVal.includes("Thanh toán 1 phần") ||
            strVal.includes("một phần")
          ) {
            cellFill = "FEF3C7";
            fontColor = "B45309";
            isBold = true;
          } else if (
            strVal.includes("Ghi nợ") ||
            strVal.includes("Chưa TT") ||
            strVal.includes("Còn nợ")
          ) {
            cellFill = "FEE2E2";
            fontColor = "991B1B";
            isBold = true;
          }
        }

        cell.s = {
          fill: { fgColor: { rgb: cellFill } },
          font: {
            bold: isBold,
            color: { rgb: fontColor },
            sz: 10.5,
            name: "Calibri",
          },
          alignment: { horizontal: horizAlign, vertical: "center" },
          border: thinBorder,
          numFmt,
        };
      }
    }
  }
};

const fitColumnsWidth = (rows, defaultWidth = 15) => {
  if (!rows || !rows.length) return [];
  const keys = Object.keys(rows[0]);
  return keys.map((key) => {
    let maxLen = key.length;
    for (let i = 0; i < Math.min(rows.length, 500); i++) {
      const val = rows[i][key];
      const strVal = val !== null && val !== undefined ? String(val) : "";
      if (strVal.length > maxLen) {
        maxLen = strVal.length;
      }
    }
    return { wch: Math.max(defaultWidth, Math.min(maxLen + 4, 45)) };
  });
};

const CustomerDateFilterPopover = memo(
  ({
    target,
    isOpen,
    toggle,
    dateFilters,
    onDateRangeChange,
    onClearDateFilter,
    placement = "bottom-start",
    rangesPosition = "right",
  }) => {
    const definedRanges = useMemo(
      () => [
        {
          label: "Hôm nay",
          startDate: moment(),
          endDate: moment(),
        },
        {
          label: "Hôm qua",
          startDate: moment().subtract(1, "day"),
          endDate: moment().subtract(1, "day"),
        },
        {
          label: "Tuần này",
          startDate: moment().startOf("isoWeek"),
          endDate: moment().endOf("isoWeek"),
        },
        {
          label: "Tuần trước",
          startDate: moment().subtract(1, "week").startOf("isoWeek"),
          endDate: moment().subtract(1, "week").endOf("isoWeek"),
        },
        {
          label: "Tháng này",
          startDate: moment().startOf("month"),
          endDate: moment().endOf("month"),
        },
        {
          label: "Tháng trước",
          startDate: moment().subtract(1, "month").startOf("month"),
          endDate: moment().subtract(1, "month").endOf("month"),
        },
        {
          label: "Tất cả",
          startDate: moment("1900-01-01"),
          endDate: moment("1900-01-01"),
        },
      ],
      [],
    );

    const initialRange = useMemo(() => {
      const hasFilter = dateFilters && !dateFilters.all && dateFilters.date_start;
      return {
        startDate: hasFilter ? moment(dateFilters.date_start) : moment(),
        endDate: hasFilter
          ? moment(dateFilters.date_end || dateFilters.date_start)
          : moment(),
      };
    }, [dateFilters]);

    const [draftRange, setDraftRange] = useState(initialRange);
    const draftRangeRef = useRef(initialRange);
    const hasSelectedDate = useRef(false);

    useEffect(() => {
      if (isOpen) {
        setDraftRange(initialRange);
        draftRangeRef.current = initialRange;
        hasSelectedDate.current = false;
      }
    }, [isOpen, initialRange]);

    const handleRangeChange = (range, interaction = {}) => {
      hasSelectedDate.current = true;
      if (interaction.source === "preset") {
        const startStr = range.startDate.format("YYYY-MM-DD");
        const endStr = range.endDate.format("YYYY-MM-DD");
        if (startStr === "1900-01-01" && endStr === "1900-01-01") {
          if (onClearDateFilter) onClearDateFilter();
        } else if (onDateRangeChange) {
          onDateRangeChange(range, interaction);
        }
        toggle();
        return;
      }

      if (interaction.source === "start") {
        const next = {
          startDate: range.startDate,
          endDate: range.startDate,
        };
        draftRangeRef.current = next;
        setDraftRange(next);
        return;
      }

      if (interaction.source === "end") {
        let s = range.startDate;
        let e = range.endDate;
        if (s.isAfter(e)) {
          [s, e] = [e, s];
        }
        const next = { startDate: s, endDate: e };
        draftRangeRef.current = next;
        setDraftRange(next);
        if (onDateRangeChange) {
          onDateRangeChange({ startDate: s, endDate: e }, interaction);
        }
        toggle();
      }
    };

    const handleApply = () => {
      const currentDraft = draftRangeRef.current || draftRange;
      let s = currentDraft.startDate || moment();
      let e = currentDraft.endDate || s;
      if (s.isAfter(e)) {
        [s, e] = [e, s];
      }
      if (onDateRangeChange) {
        onDateRangeChange({ startDate: s, endDate: e });
      }
      toggle();
    };

    const draftLabel = useMemo(() => {
      if (!draftRange?.startDate) return "";
      const s = draftRange.startDate;
      const e = draftRange.endDate || s;
      if (s.format("YYYY-MM-DD") === e.format("YYYY-MM-DD")) {
        return s.format("DD/MM/YYYY");
      }
      return `${s.format("DD/MM/YYYY")} - ${e.format("DD/MM/YYYY")}`;
    }, [draftRange]);

    const handleToggle = () => {
      if (hasSelectedDate.current) {
        const currentDraft = draftRangeRef.current || draftRange;
        if (currentDraft?.startDate) {
          let s = currentDraft.startDate;
          let e = currentDraft.endDate || s;
          if (s.isAfter(e)) [s, e] = [e, s];
          if (onDateRangeChange) {
            onDateRangeChange({ startDate: s, endDate: e });
          }
        }
      }
      toggle();
    };

    return (
      <Popover
        trigger="legacy"
        isOpen={isOpen}
        target={target}
        toggle={handleToggle}
        placement={placement}
        className="customer-modern-date-popover"
      >
        <PopoverBody>
          <div className="custom-daterange__container">
            <DateRangePicker
              initialDateRange={draftRange}
              open={true}
              onChange={handleRangeChange}
              rangesPosition={rangesPosition}
              definedRanges={definedRanges}
            />
          </div>
          <div className="customer-modern-date-popover-footer">
            <div className="customer-modern-date-popover-text">
              <span className="text-muted small mr-1">Đã chọn:</span>
              <strong className="text-primary">{draftLabel}</strong>
            </div>
            <div className="customer-modern-date-popover-actions">
              <button
                type="button"
                className="btn btn-outline-secondary btn-xs mr-2"
                onClick={() => {
                  if (onClearDateFilter) onClearDateFilter();
                  toggle();
                }}
              >
                Tất cả
              </button>
              <button
                type="button"
                className="btn btn-primary btn-xs font-weight-bold px-3"
                onClick={handleApply}
              >
                Áp dụng
              </button>
            </div>
          </div>
        </PopoverBody>
      </Popover>
    );
  },
);

const CustomerModernDashboard = ({
  page,
  data,
  dataAll,
  dataTags,
  summary,
  activity,
  products,
  activityLoading,
  productLoading,
  selectedItems,
  activeCustomerId,
  filter,
  displayMode,
  changedCount,
  canCreate,
  canUpdate,
  canDelete,
  onAdd,
  onSearchChange,
  onFilterData,
  onChangePage,
  onChangePageSize,
  onChangeDisplayMode,
  onSelectCustomer,
  onToggleCustomer,
  onSelectAll,
  onUpdateData,
  onUpdateCustomer,
  onDeleteCustomer,
  onPayDebt,
  onBulkDelete,
  onSaveChanges,
  onRefreshActivity,
  onRefreshProducts,
  dateFilters,
  onDateRangeChange,
  onSetDatePreset,
  onClearDateFilter,
}) => {
  const [search, setSearch] = useState(page.search || "");
  const [rankFilter, setRankFilter] = useState("");
  const [debtFilter, setDebtFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [mobileDatePickerOpen, setMobileDatePickerOpen] = useState(false);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [quickDebtCustomer, setQuickDebtCustomer] = useState(null);
  const [quickDebtAmount, setQuickDebtAmount] = useState("");
  const [quickDebtError, setQuickDebtError] = useState("");
  const [quickDebtSubmitting, setQuickDebtSubmitting] = useState(false);
  const [chipScrollState, setChipScrollState] = useState({ hasOverflow: false, isAtEnd: true });
  const didMountSearch = useRef(false);
  const chipScrollRef = useRef(null);

  const isDateFiltered = Boolean(dateFilters && !dateFilters.all && dateFilters.date_start);
  const dateLabel = useMemo(() => {
    if (!isDateFiltered) return "Chọn ngày";
    const start = moment(dateFilters.date_start);
    const end = moment(dateFilters.date_end || dateFilters.date_start);
    const today = moment().format("YYYY-MM-DD");
    const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");

    if (dateFilters.date_start === today && (dateFilters.date_end === today || !dateFilters.date_end)) {
      return `Hôm nay (${start.format("DD/MM")})`;
    }
    if (dateFilters.date_start === yesterday && (dateFilters.date_end === yesterday || !dateFilters.date_end)) {
      return `Hôm qua (${start.format("DD/MM")})`;
    }
    if (dateFilters.date_start === dateFilters.date_end) {
      return start.format("DD/MM/YYYY");
    }
    return `${start.format("DD/MM")} - ${end.format("DD/MM")}`;
  }, [dateFilters, isDateFiltered]);

  const chipDragRef = useRef({
    isDragging: false,
    moved: false,
    hasPointerCapture: false,
    pointerId: null,
    scrollLeft: 0,
    startX: 0,
  });

  const updateChipScrollState = useCallback(() => {
    const chipScroll = chipScrollRef.current;
    if (!chipScroll) return;

    const maxScroll = Math.max(0, chipScroll.scrollWidth - chipScroll.clientWidth);
    const nextState = {
      hasOverflow: maxScroll > 1,
      isAtEnd: chipScroll.scrollLeft >= maxScroll - 1,
    };

    setChipScrollState((prevState) =>
      prevState.hasOverflow === nextState.hasOverflow && prevState.isAtEnd === nextState.isAtEnd
        ? prevState
        : nextState
    );
  }, []);

  useEffect(() => {
    setSearch(page.search || "");
  }, [page.search]);

  useEffect(() => {
    if (!didMountSearch.current) {
      didMountSearch.current = true;
      return undefined;
    }

    const timer = setTimeout(() => {
      onSearchChange(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search, onSearchChange]);

  const allCustomers = safeArray(dataAll);
  const tableCustomers = safeArray(data);
  const tags = useMemo(() => getUniqueTags(dataTags), [dataTags]);
  const rankOptions = useMemo(() => {
    const ranks = new Set();
    allCustomers.forEach((customer) => {
      if (customer.armorial) ranks.add(customer.armorial);
    });
    return Array.from(ranks);
  }, [allCustomers]);

  const activeCustomer = useMemo(() => {
    return (
      tableCustomers.find((customer) => customer.id === activeCustomerId) ||
      allCustomers.find((customer) => customer.id === activeCustomerId) ||
      tableCustomers[0] ||
      null
    );
  }, [activeCustomerId, allCustomers, tableCustomers]);

  const summaryData = summary || {};
  const delta = summaryData.delta_percent || {};
  const statCards = [
    {
      key: "total_customers",
      label: "Tổng khách hàng",
      value: summaryData.total_customers ?? allCustomers.length,
      delta: delta.total_customers,
      icon: "simple-icon-people",
      iconColor: "#0d6efd",
      iconBg: "rgba(13, 110, 253, 0.12)",
      tone: "blue",
    },
    {
      key: "total_spent",
      label: "Tổng chi tiêu",
      value: formatMoney(summaryData.total_spent || 0),
      delta: delta.total_spent,
      icon: "iconsminds-coins",
      iconColor: "#198754",
      iconBg: "rgba(25, 135, 84, 0.12)",
      tone: "green",
    },
    {
      key: "total_debt",
      label: "Công nợ",
      value: formatMoney(summaryData.total_debt || 0),
      delta: delta.total_debt,
      icon: "simple-icon-briefcase",
      iconColor: "#dc3545",
      iconBg: "rgba(220, 53, 69, 0.12)",
      tone: "red",
    },
    // {
    //   key: "vip_count",
    //   label: "Khách hàng VIP",
    //   value: summaryData.vip_count ?? allCustomers.filter(isVipCustomer).length,
    //   delta: delta.vip_count,
    //   icon: "simple-icon-badge",
    //   tone: "purple",
    // },
    {
      key: "new_customers",
      label: "Khách hàng mới",
      value: summaryData.new_customers ?? allCustomers.filter(isNewCustomer).length,
      delta: delta.new_customers,
      icon: "simple-icon-user-follow",
      iconColor: "#0284c7",
      iconBg: "rgba(2, 132, 199, 0.12)",
      tone: "sky",
    },
  ];

  const chipCounts = {
    all: allCustomers.length,
    debt: allCustomers.filter((customer) => safeNumber(customer.debt) > 0).length,
    no_debt: allCustomers.filter((customer) => safeNumber(customer.debt) === 0).length,
    vip: allCustomers.filter(isVipCustomer).length,
    new: allCustomers.filter(isNewCustomer).length,
    sleeping: allCustomers.filter(isSleepingCustomer).length,
  };
  const chipCountSignature = quickFilters.map((item) => chipCounts[item.key]).join("|");

  useEffect(() => {
    const timer = window.setTimeout(updateChipScrollState, 0);
    const chipScroll = chipScrollRef.current;
    let resizeObserver;

    if (typeof ResizeObserver !== "undefined" && chipScroll) {
      resizeObserver = new ResizeObserver(updateChipScrollState);
      resizeObserver.observe(chipScroll);
      if (chipScroll.firstElementChild) resizeObserver.observe(chipScroll.firstElementChild);
    }

    window.addEventListener("resize", updateChipScrollState);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", updateChipScrollState);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [chipCountSignature, updateChipScrollState]);

  const activeFilterKey = filter ? `${filter.column}:${filter.value}` : "all";

  const resetFieldFilters = () => {
    setRankFilter("");
    setDebtFilter("");
    setTagFilter("");
  };

  const applyQuickFilter = (key) => {
    resetFieldFilters();
    if (key === "all") {
      if (onClearDateFilter) onClearDateFilter();
      return onFilterData("", "");
    }
    if (key === "debt") return onFilterData("debt", "have_debt");
    if (key === "no_debt") return onFilterData("debt", "");
    if (key === "vip") return onFilterData("segment", "vip");
    if (key === "new") return onFilterData("segment", "new");
    if (key === "sleeping") return onFilterData("segment", "sleeping");
    return onFilterData("", "");
  };

  const handleChipPointerDown = useCallback((event) => {
    const chipScroll = event.currentTarget;
    const maxScroll = chipScroll.scrollWidth - chipScroll.clientWidth;

    if ((event.pointerType === "mouse" && event.button !== 0) || maxScroll <= 1) return;

    chipDragRef.current = {
      isDragging: true,
      moved: false,
      hasPointerCapture: false,
      pointerId: event.pointerId,
      scrollLeft: chipScroll.scrollLeft,
      startX: event.clientX,
    };
  }, []);

  const handleChipPointerMove = useCallback(
    (event) => {
      const chipDrag = chipDragRef.current;
      if (!chipDrag.isDragging) return;

      const distance = event.clientX - chipDrag.startX;
      if (Math.abs(distance) < 3) return;

      if (!chipDrag.moved) {
        chipDrag.moved = true;
        event.currentTarget.classList.add("is-dragging");

        if (event.currentTarget.setPointerCapture) {
          try {
            event.currentTarget.setPointerCapture(chipDrag.pointerId);
            chipDrag.hasPointerCapture = true;
          } catch (error) {
            chipDrag.hasPointerCapture = false;
          }
        }
      }

      event.preventDefault();
      event.currentTarget.scrollLeft = chipDrag.scrollLeft - distance;
      updateChipScrollState();
    },
    [updateChipScrollState]
  );

  const finishChipDrag = useCallback(
    (event) => {
      const chipDrag = chipDragRef.current;
      if (!chipDrag.isDragging) return;

      chipDrag.isDragging = false;
      event.currentTarget.classList.remove("is-dragging");

      if (
        chipDrag.hasPointerCapture &&
        chipDrag.pointerId !== null &&
        event.currentTarget.releasePointerCapture &&
        (!event.currentTarget.hasPointerCapture || event.currentTarget.hasPointerCapture(chipDrag.pointerId))
      ) {
        event.currentTarget.releasePointerCapture(chipDrag.pointerId);
      }

      updateChipScrollState();
      window.setTimeout(() => {
        chipDragRef.current.moved = false;
      }, 80);
    },
    [updateChipScrollState]
  );

  const handleChipScroll = useCallback(() => {
    updateChipScrollState();
  }, [updateChipScrollState]);

  const handleChipWheel = useCallback(
    (event) => {
      const chipScroll = event.currentTarget;
      const maxScroll = chipScroll.scrollWidth - chipScroll.clientWidth;
      if (maxScroll <= 1) return;

      const delta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!delta) return;

      const nextScroll = Math.max(0, Math.min(maxScroll, chipScroll.scrollLeft + delta));
      if (nextScroll === chipScroll.scrollLeft) return;

      event.preventDefault();
      chipScroll.scrollLeft = nextScroll;
      updateChipScrollState();
    },
    [updateChipScrollState]
  );

  const handleQuickFilterClick = (event, key) => {
    if (chipDragRef.current.moved) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    applyQuickFilter(key);
  };

  const handleRankChange = (value) => {
    setRankFilter(value);
    setDebtFilter("");
    setTagFilter("");
    if (!value) return onFilterData("", "");
    return onFilterData("armorial", value);
  };

  const handleDebtChange = (value) => {
    setDebtFilter(value);
    setRankFilter("");
    setTagFilter("");
    if (!value) return onFilterData("", "");
    if (value === "no_debt") return onFilterData("debt", "");
    return onFilterData("debt", value);
  };

  const handleTagChange = (value) => {
    setTagFilter(value);
    setRankFilter("");
    setDebtFilter("");
    if (!value) return onFilterData("", "");
    return onFilterData("tags", value);
  };

  const handleExportCustomerMatrix = useCallback(
    (targetCustomer = activeCustomer) => {
      if (!targetCustomer) return;

      const customerActivity =
        targetCustomer.id === activeCustomer?.id ? activity : [];
      const matrixRows = buildDailyMatrixSheetForCustomer(
        targetCustomer,
        customerActivity,
      );
      const debtRows = buildDebtMatrixSheetForCustomer(
        targetCustomer,
        customerActivity,
      );
      const detailRows = buildTransactionDetailSheetForCustomer(
        targetCustomer,
        customerActivity,
      );

      const wsMatrix = XLSX.utils.json_to_sheet(matrixRows);
      wsMatrix["!cols"] = fitColumnsWidth(matrixRows, 15);
      applyExcelWorksheetStyles(wsMatrix, { sheetType: "matrix" });

      const wsDebt = XLSX.utils.json_to_sheet(debtRows);
      wsDebt["!cols"] = fitColumnsWidth(debtRows, 15);
      applyExcelWorksheetStyles(wsDebt, { sheetType: "debt" });

      const wsDetail = XLSX.utils.json_to_sheet(detailRows);
      wsDetail["!cols"] = fitColumnsWidth(detailRows, 15);
      applyExcelWorksheetStyles(wsDetail, { sheetType: "detail" });

      const wb = {
        Sheets: {
          "Sổ giao hàng - Tổng hợp": wsMatrix,
          "Chi tiết hàng nợ (Chưa TT)": wsDebt,
          "Bảng kê chi tiết đơn hàng": wsDetail,
        },
        SheetNames: [
          "Sổ giao hàng - Tổng hợp",
          "Chi tiết hàng nợ (Chưa TT)",
          "Bảng kê chi tiết đơn hàng",
        ],
      };

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: fileType });
      const cleanName = String(
        targetCustomer.name || targetCustomer.phone || "Khách hàng",
      )
        .replace(/[\\/:*?"<>|]/g, "_")
        .trim();
      FileSaver.saveAs(blob, `Sổ giao hàng - ${cleanName}${fileExtension}`);
    },
    [activeCustomer, activity],
  );

  const handleExport = () => {
    const targetCustomers = allCustomers.length ? allCustomers : tableCustomers;
    const sheet1Rows = buildExportRows(targetCustomers);

    const ws1 = XLSX.utils.json_to_sheet(sheet1Rows);
    ws1["!cols"] = fitColumnsWidth(sheet1Rows, 14);
    applyExcelWorksheetStyles(ws1, { sheetType: "overview" });

    const wb = {
      Sheets: {
        "Danh sách khách hàng": ws1,
      },
      SheetNames: ["Danh sách khách hàng"],
    };

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(blob, `Danh sách khách hàng${fileExtension}`);
  };

  const openQuickDebtPayment = useCallback(
    (customer) => {
      const debtAmount = safeNumber(customer && customer.debt);
      if (!customer || !canUpdate || debtAmount <= 0) return;

      setQuickDebtCustomer(customer);
      setQuickDebtAmount(String(debtAmount));
      setQuickDebtError("");
    },
    [canUpdate],
  );

  const closeQuickDebtPayment = useCallback(() => {
    if (quickDebtSubmitting) return;
    setQuickDebtCustomer(null);
    setQuickDebtAmount("");
    setQuickDebtError("");
  }, [quickDebtSubmitting]);

  const handleQuickDebtAmountChange = useCallback((event) => {
    setQuickDebtAmount(String(event.target.value || "").replace(/\D/g, ""));
    setQuickDebtError("");
  }, []);

  const handleQuickDebtSubmit = useCallback(
    (event) => {
      event.preventDefault();

      if (!quickDebtCustomer) return;

      const debtAmount = safeNumber(quickDebtCustomer.debt);
      const paymentAmount = safeNumber(quickDebtAmount);

      if (paymentAmount <= 0) {
        setQuickDebtError("Số tiền thanh toán phải lớn hơn 0.");
        return;
      }

      if (paymentAmount > debtAmount) {
        setQuickDebtError("Số tiền thanh toán không được lớn hơn dư nợ hiện tại.");
        return;
      }

      if (!onPayDebt) {
        setQuickDebtError("Chưa cấu hình thao tác thanh toán nhanh.");
        return;
      }

      setQuickDebtSubmitting(true);
      Promise.resolve(onPayDebt(quickDebtCustomer, paymentAmount))
        .then(() => {
          setQuickDebtCustomer(null);
          setQuickDebtAmount("");
          setQuickDebtError("");
        })
        .catch(() => false)
        .finally(() => {
          setQuickDebtSubmitting(false);
        });
    },
    [onPayDebt, quickDebtAmount, quickDebtCustomer],
  );

  const selectedAll =
    tableCustomers.length > 0 &&
    tableCustomers.every((customer) => selectedItems.includes(customer.id));

  const displayStart = tableCustomers.length
    ? (page.currentPage - 1) * page.selectedPageSize + 1
    : 0;
  const displayEnd = filter
    ? tableCustomers.length
    : Math.min(page.currentPage * page.selectedPageSize, page.totalItem);

  return (
    <div className="customer-modern-page">
      <div className="customer-modern-desktop d-none d-md-block">
        <div className="customer-modern-layout">
          <section className="customer-modern-main">
            <Row className="mb-3">
              {statCards.map((card) => (
                <SummaryCard key={card.key} card={card} />
              ))}
            </Row>

            {/* <Card className="mb-3">
              <CardBody>
                <div
                  className="mb-3"
                  style={{ overflowX: "auto", overflowY: "hidden" }}
                >
                  <div
                    className="d-flex align-items-center flex-nowrap justify-content-end mb-2"
                    style={{ minWidth: "max-content" }}
                  >
                    <Input
                      bsSize="sm"
                      type="select"
                      className="mr-2 flex-shrink-0"
                      style={{
                        ...STOCK_TOOLBAR_SELECT_STYLE,
                        width: 170,
                        minWidth: 170,
                        maxWidth: 170,
                      }}
                      value={filters.category_name || ""}
                      onChange={(e) => onFilterChange("category_name", e.target.value)}
                    >
                      <option value="">Danh mục: Tất cả</option>
                      {(filterOptions?.category_names || []).map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </Input>
                  </div>
                </div>
              </CardBody>
            </Card> */}
            <div className="customer-modern-filter-action-row">
              <div
                className={classnames("customer-modern-chip-scroll-shell", {
                  "has-overflow": chipScrollState.hasOverflow,
                  "at-scroll-end": chipScrollState.isAtEnd,
                })}
              >
                <div
                  ref={chipScrollRef}
                  className="customer-modern-chip-scroll"
                  onPointerDown={handleChipPointerDown}
                  onPointerMove={handleChipPointerMove}
                  onPointerUp={finishChipDrag}
                  onPointerCancel={finishChipDrag}
                  onScroll={handleChipScroll}
                  onWheel={handleChipWheel}
                >
                <div className="customer-modern-chip-row">
                  <div className="customer-modern-date-filter-wrap">
                    <button
                      type="button"
                      id="customer-modern-date-btn-desktop"
                      className={classnames("customer-modern-chip customer-modern-date-btn", {
                        active: isDateFiltered,
                      })}
                      onClick={() => setDatePickerOpen(!datePickerOpen)}
                    >
                      <i className="simple-icon-calendar mr-1" />
                      <span>{dateLabel}</span>
                      {isDateFiltered && (
                        <span
                          className="customer-modern-date-clear ml-1"
                          title="Xóa lọc ngày"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onClearDateFilter) onClearDateFilter();
                          }}
                        >
                          ×
                        </span>
                      )}
                    </button>
                    <CustomerDateFilterPopover
                      target="customer-modern-date-btn-desktop"
                      isOpen={datePickerOpen}
                      toggle={() => setDatePickerOpen(!datePickerOpen)}
                      dateFilters={dateFilters}
                      onDateRangeChange={onDateRangeChange}
                      onClearDateFilter={onClearDateFilter}
                      placement="bottom-start"
                      rangesPosition="right"
                    />
                  </div>
                  {quickFilters.map((item) => {
                    const isActive =
                      item.key === "all"
                        ? activeFilterKey === "all" && !isDateFiltered
                        : (item.key === "debt" && activeFilterKey === "debt:have_debt") ||
                          (item.key === "no_debt" && activeFilterKey === "debt:") ||
                          (item.key === "vip" && activeFilterKey === "segment:vip") ||
                          (item.key === "new" && activeFilterKey === "segment:new") ||
                          (item.key === "sleeping" && activeFilterKey === "segment:sleeping");

                    return (
                      <button
                        type="button"
                        key={item.key}
                        className={classnames("customer-modern-chip", `tone-${item.tone}`, {
                          active: isActive,
                        })}
                        onClick={(event) => handleQuickFilterClick(event, item.key)}
                      >
                        {item.label}
                        <span>{formatCount(chipCounts[item.key])}</span>
                      </button>
                    );
                  })}
                  </div>
                </div>
              </div>

              <div className="customer-modern-chip-actions">
                <Button
                  color="link"
                  size="sm"
                  className="customer-modern-soft-button"
                  style={CUSTOMER_TOOLBAR_SOFT_BUTTON_STYLE}
                  onClick={handleExport}
                >
                  <i className="simple-icon-cloud-download mr-1" />
                  Xuất Excel
                </Button>

                <Button
                  color="primary"
                  size="sm"
                  disabled={!canCreate}
                  style={CUSTOMER_TOOLBAR_PRIMARY_BUTTON_STYLE}
                  onClick={onAdd}
                >
                  + Thêm mới
                </Button>
              </div>
            </div>

            {selectedItems.length ? (
              <div className="customer-modern-bulk-bar">
                <span>Đã chọn {selectedItems.length} khách hàng</span>
                <Button
                  color="danger"
                  size="sm"
                  disabled={!canDelete}
                  style={CUSTOMER_TOOLBAR_DANGER_BUTTON_STYLE}
                  onClick={onBulkDelete}
                >
                  <i className="simple-icon-trash mr-1" />
                  Xóa ({selectedItems.length})
                </Button>
              </div>
            ) : null}

            {displayMode === "list" ? (
              <Card className="customer-modern-table-card">
                <CardBody>
                  <ContentMultiForm
                    data={allCustomers}
                    data_tags={dataTags}
                    onUpdateData={onUpdateData}
                    selectedItems={selectedItems}
                    toggleItem={onToggleCustomer}
                    onSelectAll={onSelectAll}
                  />
                  <div className="customer-modern-save-row">
                    <Button
                      color="primary"
                      disabled={!changedCount}
                      style={CUSTOMER_TOOLBAR_PRIMARY_BUTTON_STYLE}
                      onClick={onSaveChanges}
                    >
                      Lưu thay đổi {changedCount ? `(${changedCount})` : ""}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ) : (
              <Card className="customer-modern-table-card">
                <div className="customer-modern-table-wrap">
                  <table className="customer-modern-table">
                    <colgroup>
                      <col className="customer-modern-col-check" />
                      <col className="customer-modern-col-customer" />
                      <col className="customer-modern-col-rank" />
                      <col className="customer-modern-col-spent" />
                      <col className="customer-modern-col-orders" />
                      <col className="customer-modern-col-last-order" />
                      <col className="customer-modern-col-debt" />
                      <col className="customer-modern-col-points" />
                      <col className="customer-modern-col-tags" />
                      <col className="customer-modern-col-action" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="check-col">
                          <CustomInput
                            type="checkbox"
                            id="customer-modern-check-all"
                            label=""
                            checked={selectedAll}
                            onChange={() => onSelectAll(true)}
                          />
                        </th>
                        <th className="customer-col">Khách hàng</th>
                        <th className="rank-col">Hạng</th>
                        <th className="spent-col">{isDateFiltered ? "Chi tiêu trong kỳ" : "Tổng chi tiêu"}</th>
                        <th className="orders-col">{isDateFiltered ? "Số đơn trong kỳ" : "Số đơn hàng"}</th>
                        <th className="last-order-col">Lần mua gần nhất</th>
                        <th className="debt-col">{isDateFiltered ? "Nợ trong kỳ" : "Công nợ"}</th>
                        <th className="points-col">Điểm tích lũy</th>
                        <th className="tags-col">Tags</th>
                        <th className="action-col" aria-label="Thao tác" />
                      </tr>
                    </thead>
                    <tbody>
                      {tableCustomers.length ? (
                        tableCustomers.map((customer) => (
                          <CustomerTableRow
                            key={customer.id}
                            customer={customer}
                            active={activeCustomer?.id === customer.id}
                            selected={selectedItems.includes(customer.id)}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                            isDateFiltered={isDateFiltered}
                            onClick={() => onSelectCustomer(customer)}
                            onToggle={(event) => {
                              event.stopPropagation();
                              onToggleCustomer(customer.id, event);
                            }}
                            onUpdate={(event) => {
                              event.stopPropagation();
                              onUpdateCustomer(customer);
                            }}
                            onDelete={(event) => {
                              event.stopPropagation();
                              onDeleteCustomer(customer);
                            }}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan="10" className="customer-modern-empty-cell">
                            Chưa có dữ liệu khách hàng
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="customer-modern-pagination">
                  <div>
                    Hiển thị {displayStart} - {displayEnd} trên tổng {filter ? tableCustomers.length : page.totalItem}
                  </div>
                  {!filter ? (
                    <Pagination
                      currentPage={page.currentPage}
                      totalPage={page.totalPage}
                      onChangePage={onChangePage}
                    />
                  ) : null}
                  <Input
                    type="select"
                    bsSize="sm"
                    value={page.selectedPageSize}
                    style={CUSTOMER_TABLE_SELECT_STYLE}
                    onChange={(event) => onChangePageSize(Number(event.target.value))}
                  >
                    {page.pageSizes.map((size) => (
                      <option key={size} value={size}>
                        {size} / trang
                      </option>
                    ))}
                  </Input>
                </div>
              </Card>
            )}
          </section>

          <aside className="customer-modern-side">
            <CustomerDetailPanel
              customer={activeCustomer}
              activity={activity}
              products={products}
              loading={activityLoading}
              productLoading={productLoading}
              canUpdate={canUpdate}
              canDelete={canDelete}
              isDateFiltered={isDateFiltered}
              onRefreshActivity={onRefreshActivity}
              onRefreshProducts={onRefreshProducts}
              onUpdateCustomer={onUpdateCustomer}
              onDeleteCustomer={onDeleteCustomer}
              onOpenDebtPayment={openQuickDebtPayment}
              onExportCustomerMatrix={handleExportCustomerMatrix}
            />
          </aside>
        </div>
      </div>

      <div className="customer-modern-mobile d-md-none">
        <div className="customer-modern-mobile-header">
          <div>
            <h1>Khách hàng</h1>
            <span>{formatCount(allCustomers.length || page.totalItem)} hồ sơ</span>
          </div>
          <Button
            color="primary"
            size="sm"
            disabled={!canCreate}
            style={CUSTOMER_TOOLBAR_PRIMARY_BUTTON_STYLE}
            onClick={onAdd}
          >
            <i className="simple-icon-plus" />
          </Button>
        </div>

        <div className="customer-modern-search mobile">
          <i className="simple-icon-magnifier" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Tìm khách hàng..."
            aria-label="Tìm khách hàng"
          />
        </div>

        <div className="customer-modern-mobile-stats">
          {statCards.slice(0, 4).map((card) => (
            <button type="button" key={card.key}>
              <strong>{card.value}</strong>
              <span>{card.label}</span>
            </button>
          ))}
        </div>

        <div className="customer-modern-chip-row mobile">
          <div className="customer-modern-date-filter-wrap">
            <button
              type="button"
              id="customer-modern-date-btn-mobile"
              className={classnames("customer-modern-chip customer-modern-date-btn", {
                active: isDateFiltered,
              })}
              onClick={() => setMobileDatePickerOpen(!mobileDatePickerOpen)}
            >
              <i className="simple-icon-calendar mr-1" />
              <span>{dateLabel}</span>
              {isDateFiltered && (
                <span
                  className="customer-modern-date-clear ml-1"
                  title="Xóa lọc ngày"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onClearDateFilter) onClearDateFilter();
                  }}
                >
                  ×
                </span>
              )}
            </button>
            <CustomerDateFilterPopover
              target="customer-modern-date-btn-mobile"
              isOpen={mobileDatePickerOpen}
              toggle={() => setMobileDatePickerOpen(!mobileDatePickerOpen)}
              dateFilters={dateFilters}
              onDateRangeChange={onDateRangeChange}
              onSetDatePreset={onSetDatePreset}
              onClearDateFilter={onClearDateFilter}
              placement="bottom-start"
              rangesPosition="top"
            />
          </div>
          {quickFilters.map((item) => (
            <button
              type="button"
              key={item.key}
              className={classnames("customer-modern-chip", {
                active: item.key === "all" ? activeFilterKey === "all" && !isDateFiltered : undefined,
              })}
              onClick={() => applyQuickFilter(item.key)}
            >
              {item.label}
              <span>{formatCount(chipCounts[item.key])}</span>
            </button>
          ))}
        </div>

        <div className="customer-modern-mobile-list">
          {tableCustomers.length ? (
            tableCustomers.map((customer) => (
              <MobileCustomerCard
                key={customer.id}
                customer={customer}
                selected={selectedItems.includes(customer.id)}
                canUpdate={canUpdate}
                canDelete={canDelete}
                isDateFiltered={isDateFiltered}
                onOpen={() => {
                  onSelectCustomer(customer);
                  setMobileDetailOpen(true);
                }}
                onToggle={(event) => {
                  event.stopPropagation();
                  onToggleCustomer(customer.id, event);
                }}
                onUpdate={(event) => {
                  event.stopPropagation();
                  onUpdateCustomer(customer);
                }}
                onDelete={(event) => {
                  event.stopPropagation();
                  onDeleteCustomer(customer);
                }}
              />
            ))
          ) : (
            <div className="customer-modern-mobile-empty">Chưa có dữ liệu khách hàng</div>
          )}
        </div>

        {!filter ? (
          <Pagination
            currentPage={page.currentPage}
            totalPage={page.totalPage}
            onChangePage={onChangePage}
          />
        ) : null}

        <Modal
          isOpen={mobileDetailOpen}
          toggle={() => setMobileDetailOpen(false)}
          wrapClassName="modal-bottom-sheet customer-modern-mobile-detail-modal"
          backdrop
        >
          <ModalBody>
            <CustomerDetailPanel
              customer={activeCustomer}
              activity={activity}
              products={products}
              loading={activityLoading}
              productLoading={productLoading}
              canUpdate={canUpdate}
              canDelete={canDelete}
              compact
              isDateFiltered={isDateFiltered}
              onClose={() => setMobileDetailOpen(false)}
              onRefreshActivity={onRefreshActivity}
              onRefreshProducts={onRefreshProducts}
              onUpdateCustomer={onUpdateCustomer}
              onDeleteCustomer={onDeleteCustomer}
              onOpenDebtPayment={openQuickDebtPayment}
              onExportCustomerMatrix={handleExportCustomerMatrix}
            />
          </ModalBody>
        </Modal>
      </div>

      <QuickDebtPaymentModal
        customer={quickDebtCustomer}
        amount={quickDebtAmount}
        error={quickDebtError}
        submitting={quickDebtSubmitting}
        onChangeAmount={handleQuickDebtAmountChange}
        onUseFullDebt={() => {
          setQuickDebtAmount(String(safeNumber(quickDebtCustomer && quickDebtCustomer.debt)));
          setQuickDebtError("");
        }}
        onSubmit={handleQuickDebtSubmit}
        onClose={closeQuickDebtPayment}
      />
    </div>
  );
};

const QuickDebtPaymentModal = ({
  customer,
  amount,
  error,
  submitting,
  onChangeAmount,
  onUseFullDebt,
  onSubmit,
  onClose,
}) => {
  const debtAmount = safeNumber(customer && customer.debt);
  const amountValue = amount ? AddCommaNumber(safeNumber(amount)) : "";

  return (
    <Modal
      isOpen={Boolean(customer)}
      toggle={submitting ? undefined : onClose}
      // centered
      className="customer-modern-debt-payment-modal"
    >
      <ModalHeader toggle={submitting ? undefined : onClose}>
        Thanh toán công nợ
      </ModalHeader>
      <ModalBody>
        <form id="customer-modern-debt-payment-form" onSubmit={onSubmit}>
          <div className="customer-modern-debt-payment-summary">
            <span>Khách hàng</span>
            <strong>{getCustomerName(customer)}</strong>
            <span>Dư nợ hiện tại</span>
            <strong className="debt">{formatMoney(debtAmount)}</strong>
          </div>

          <label className="customer-modern-debt-payment-field" htmlFor="customer-modern-debt-payment-amount">
            <span>Số tiền thanh toán</span>
            <Input
              id="customer-modern-debt-payment-amount"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={amountValue}
              disabled={submitting}
              onChange={onChangeAmount}
              placeholder="Nhập số tiền"
            />
          </label>

          <button
            type="button"
            className="customer-modern-debt-payment-full"
            disabled={submitting || debtAmount <= 0}
            onClick={onUseFullDebt}
          >
            Tất toán toàn bộ {formatMoney(debtAmount)}
          </button>

          {error ? <div className="customer-modern-debt-payment-error">{error}</div> : null}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={submitting} onClick={onClose}>
          Hủy
        </Button>
        <Button
          color="primary"
          type="submit"
          form="customer-modern-debt-payment-form"
          disabled={submitting || safeNumber(amount) <= 0}
        >
          {submitting ? "Đang xử lý..." : "Thanh toán"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const SummaryCard = ({ card }) => {
  const delta = Number(card.delta || 0);
  return (
    <Col key={card.key} md="6" lg="3" className="mb-2">
      <Card>
        <CardBody className="py-3 d-flex align-items-center">
          <div
            className="d-flex align-items-center justify-content-center mr-2"
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              backgroundColor: card.iconBg,
            }}
          >
            <i
              className={card.icon}
              style={{ color: card.iconColor, fontSize: "1.15rem" }}
            />
          </div>
          <div>
            <div className="text-muted text-small mb-2">{card.label}</div>
            <div className="h6 mb-0">{card.value}</div>
            {/* <small className={classnames({ positive: delta >= 0, negative: delta < 0 })}>
              {delta >= 0 ? "+" : ""}
                {delta}% so với tháng trước
            </small> */}
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

const CustomerTableRow = ({
  customer,
  active,
  selected,
  canUpdate,
  canDelete,
  isDateFiltered,
  onClick,
  onToggle,
  onUpdate,
  onDelete,
}) => (
  <tr className={classnames({ active })} onClick={onClick}>
    <td className="check-col">
      <CustomInput
        type="checkbox"
        id={`customer-modern-check-${customer.id}`}
        label=""
        checked={selected}
        onChange={onToggle}
      />
    </td>
    <td className="customer-col">
      <div className="customer-modern-name-cell">
        <CustomerAvatar customer={customer} />
        <div>
          <strong>{getCustomerName(customer)}</strong>
          <span>{customer.phone ? AddCommaPhoneNumber(customer.phone) : "--"}</span>
        </div>
      </div>
    </td>
    <td className="rank-col">
      {customer.armorial ? (
        <span className={classnames("customer-modern-rank", `tone-${getRankTone(customer.armorial)}`)}>
          {customer.armorial}
        </span>
      ) : (
        <span className="customer-modern-muted">--</span>
      )}
    </td>
    <td className="spent-col">{formatMoney(customer.total_spent || 0)}</td>
    <td className="orders-col">
      {formatCount(customer.order_count !== undefined ? customer.order_count : customer.used)}
    </td>
    <td className="last-order-col">{formatDateTime(customer.last_order_date, customer.last_order_time)}</td>
    <td className={classnames("debt-col", safeNumber(customer.debt) > 0 ? "customer-modern-debt" : "customer-modern-success")}>
      <div>{formatMoney(customer.debt || 0)}</div>
      {isDateFiltered && customer.all_time_debt !== undefined && (
        <div style={{ fontSize: "11px", color: "#64748b" }} title="Tổng công nợ tích lũy toàn thời gian của khách hàng">
          Tổng nợ: {formatMoney(customer.all_time_debt)}
        </div>
      )}
      {safeNumber(customer.debt_limit) > 0 && (
        <div style={{ fontSize: "11px", color: "#888" }}>
          HM: {formatMoney(customer.debt_limit)}
        </div>
      )}
    </td>
    <td className="points-col">{formatCount(customer.point)}</td>
    <td className="tags-col">
      <TagList tags={customer.tags} />
    </td>
    <td className="customer-modern-action-col action-col">
      <UncontrolledDropdown>
        <DropdownToggle
          color="link"
          size="sm"
          className="customer-modern-icon-button"
          style={CUSTOMER_ICON_BUTTON_STYLE}
        >
          <i className="simple-icon-options-vertical" />
        </DropdownToggle>
        <DropdownMenu right>
          <DropdownItem disabled={!canUpdate} onClick={onUpdate}>
            <i className="simple-icon-note mr-2" />
            Sửa
          </DropdownItem>
          <DropdownItem disabled={!canDelete} onClick={onDelete} className="text-danger">
            <i className="simple-icon-trash mr-2" />
            Xóa
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    </td>
  </tr>
);

const MobileCustomerCard = ({
  customer,
  selected,
  canUpdate,
  canDelete,
  isDateFiltered,
  onOpen,
  onToggle,
  onUpdate,
  onDelete,
}) => (
  <button type="button" className="customer-modern-mobile-card" onClick={onOpen}>
    <CustomInput
      type="checkbox"
      id={`customer-modern-mobile-check-${customer.id}`}
      label=""
      checked={selected}
      onChange={onToggle}
    />
    <CustomerAvatar customer={customer} />
    <div className="customer-modern-mobile-card-body">
      <strong>{getCustomerName(customer)}</strong>
      <span>{customer.phone ? AddCommaPhoneNumber(customer.phone) : "--"}</span>
      <small>
        {formatMoney(customer.total_spent || 0)} ·{" "}
        {formatCount(customer.order_count !== undefined ? customer.order_count : customer.used)} đơn
      </small>
    </div>
    <UncontrolledDropdown>
      <DropdownToggle
        color="link"
        size="sm"
        className="customer-modern-icon-button"
        style={CUSTOMER_ICON_BUTTON_STYLE}
        onClick={(e) => e.stopPropagation()}
      >
        <i className="simple-icon-options-vertical" />
      </DropdownToggle>
      <DropdownMenu right>
        <DropdownItem disabled={!canUpdate} onClick={onUpdate}>
          Sửa
        </DropdownItem>
        <DropdownItem disabled={!canDelete} onClick={onDelete} className="text-danger">
          Xóa
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  </button>
);

const CustomerAvatar = ({ customer }) => (
  <span className={classnames("customer-modern-avatar", { vip: isVipCustomer(customer) })}>
    {getInitials(customer)}
  </span>
);

const TagList = ({ tags }) => {
  const visibleTags = safeArray(tags).slice(0, 3);
  if (!visibleTags.length) return <span className="customer-modern-muted">--</span>;

  return (
    <span className="customer-modern-tags">
      {visibleTags.map((tag) => (
        <span key={tag.value || tag.label}>{tag.label}</span>
      ))}
    </span>
  );
};

const CustomerContactAction = ({ href, icon, label, onClick, disabled, tone }) => {
  const isDisabled = Boolean(disabled || (!href && !onClick));
  const className = classnames("customer-modern-contact-action", {
    [`tone-${tone}`]: tone,
    "customer-modern-contact-disabled": isDisabled,
  });
  const content = (
    <>
      <i className={icon} />
      <span>{label}</span>
    </>
  );

  if (isDisabled || onClick) {
    return (
      <button
        type="button"
        className={className}
        aria-label={isDisabled ? `${label} chưa khả dụng` : label}
        disabled={isDisabled}
        onClick={isDisabled ? undefined : onClick}
      >
        {content}
      </button>
    );
  }

  return (
    <a href={href} aria-label={label} className={className}>
      {content}
    </a>
  );
};

const CustomerDetailPanel = ({
  customer,
  activity,
  products,
  loading,
  productLoading,
  compact,
  canUpdate,
  canDelete,
  isDateFiltered = false,
  onClose,
  onRefreshActivity,
  onRefreshProducts,
  onUpdateCustomer,
  onDeleteCustomer,
  onOpenDebtPayment,
  onExportCustomerMatrix,
}) => {
  const [activeTab, setActiveTab] = useState("info");
  const customerId = customer && customer.id;
  const tabsRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const handleTabsScroll = useCallback(() => {
    if (tabsRef.current) {
      tabsRef.current.classList.add("is-scrolling");
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        if (tabsRef.current) {
          tabsRef.current.classList.remove("is-scrolling");
        }
      }, 1500);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setActiveTab("info");
  }, [customerId]);

  if (!customer) {
    return (
      <Card className="customer-modern-detail-card">
        <CardBody className="customer-modern-detail-empty">Chọn một khách hàng để xem chi tiết</CardBody>
      </Card>
    );
  }

  const phone = `${customer.phone || ""}`.trim();
  const email = `${customer.email || ""}`.trim();
  const debtAmount = safeNumber(customer.debt);
  const customerActivity = safeArray(activity);
  const customerProducts = safeArray(products);
  const debtActivity = customerActivity.filter(isDebtActivity);

  return (
    <Card className={classnames("customer-modern-detail-card", { compact })}>
      <CardBody>
        {/* <div className="customer-modern-detail-close-row">
          {compact ? (
            <button type="button" onClick={onClose} aria-label="Đóng">
              <i className="simple-icon-close" />
            </button>
          ) : null}
        </div> */}

        <div className="customer-modern-detail-head">
          <CustomerAvatar customer={customer} />
          <div>
            <h2>{getCustomerName(customer)}</h2>
            <span>{phone ? AddCommaPhoneNumber(phone) : "--"}</span>
          </div>
          {customer.armorial ? (
            <span className={classnames("customer-modern-rank", `tone-${getRankTone(customer.armorial)}`)}>
              {customer.armorial}
            </span>
          ) : null}
        </div>

        <div className="customer-modern-detail-actions">
          <CustomerContactAction
            href={phone ? `sms:${phone}` : ""}
            icon="simple-icon-bubble"
            label="Gửi tin nhắn"
          />
          <CustomerContactAction
            href={phone ? `tel:${phone}` : ""}
            icon="simple-icon-phone"
            label="Gọi điện"
          />
          <CustomerContactAction
            href={email ? `mailto:${email}` : ""}
            icon="simple-icon-envelope"
            label="Email"
          />
          <CustomerContactAction
            icon="simple-icon-wallet"
            label="Thanh toán"
            tone="debt"
            disabled={!canUpdate || debtAmount <= 0}
            onClick={() => onOpenDebtPayment && onOpenDebtPayment(customer)}
          />
          <CustomerContactAction
            icon="simple-icon-cloud-download"
            label="Sổ giao hàng"
            onClick={() =>
              onExportCustomerMatrix && onExportCustomerMatrix(customer)
            }
          />
        </div>

        <div className="customer-modern-detail-metrics">
          <div>
            <span>Tổng chi tiêu {isDateFiltered ? "(kỳ này)" : ""}</span>
            <strong>{formatMoney(customer.total_spent || 0)}</strong>
          </div>
          <div>
            <span>Số đơn hàng {isDateFiltered ? "(kỳ này)" : ""}</span>
            <strong>
              {formatCount(
                customer.order_count !== undefined
                  ? customer.order_count
                  : customer.used,
              )}
            </strong>
          </div>
          <div>
            <span>{isDateFiltered ? "Nợ trong kỳ" : "Công nợ"}</span>
            <strong>{formatMoney(customer.debt || 0)}</strong>
            {isDateFiltered && customer.all_time_debt !== undefined && (
              <small className="text-muted d-block" style={{ fontSize: "11px" }}>
                Tổng nợ: {formatMoney(customer.all_time_debt)}
              </small>
            )}
            {safeNumber(customer.debt_limit) > 0 && (
              <small className="text-muted d-block" style={{ fontSize: "11px" }}>
                Hạn mức: {formatMoney(customer.debt_limit)}
              </small>
            )}
          </div>
          <div>
            <span>Điểm tích lũy</span>
            <strong>{formatCount(customer.point)}</strong>
          </div>
        </div>

        <div
          ref={tabsRef}
          onScroll={handleTabsScroll}
          className="customer-modern-detail-tabs"
        >
          {detailTabs.map((tab) => (
            <button
              type="button"
              key={tab.key}
              className={classnames({ active: activeTab === tab.key })}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "info" ? (
          <div className="customer-modern-info-list">
            {/* <InfoRow icon="simple-icon-tag" label="Mã khách hàng" value={customer.id} /> */}
            <InfoRow icon="simple-icon-user" label="Họ và tên" value={customer.name} />
            <InfoRow icon="simple-icon-phone" label="Điện thoại" value={customer.phone} />
            <InfoRow icon="simple-icon-envelope" label="Email" value={customer.email} />
            <InfoRow icon="simple-icon-location-pin" label="Địa chỉ" value={customer.address} />
            <InfoRow icon="simple-icon-calendar" label="Ngày tạo" value={formatDateTime(customer.createdAt)} />
            <InfoRow icon="simple-icon-clock" label="Lần mua gần nhất" value={formatDateTime(customer.last_order_date, customer.last_order_time)} />
          </div>
        ) : activeTab === "orders" ? (
          <OrderActivityList
            activity={customerActivity}
            loading={loading}
            onRefreshActivity={onRefreshActivity}
          />
        ) : activeTab === "products" ? (
          <ProductUsageList
            products={customerProducts}
            customer={customer}
            loading={productLoading}
            onRefreshProducts={onRefreshProducts}
            onExportCustomerMatrix={onExportCustomerMatrix}
          />
        ) : activeTab === "debt" ? (
          <DebtActivityList
            activity={debtActivity}
            customer={customer}
            loading={loading}
            onRefreshActivity={onRefreshActivity}
            isDateFiltered={isDateFiltered}
          />
        ) : activeTab === "points" ? (
          <div className="customer-modern-points-pane">
            <strong>{formatCount(customer.point)} điểm</strong>
            <span>
              Tương ứng {formatMoney(safeNumber(customer.point) * 1000)} theo quy đổi mặc định.
            </span>
            <TagList tags={customer.tags} />
          </div>
        ) : (
          <ActivityList
            activity={customerActivity}
            loading={loading}
            onRefreshActivity={onRefreshActivity}
          />
        )}

        {/* <div className="customer-modern-detail-recent">
          <div>
            <h3>Hoạt động gần đây</h3>
            <button type="button" onClick={onRefreshActivity}>
              Xem tất cả
            </button>
          </div>
          <ActivityList activity={customerActivity.slice(0, 2)} loading={loading} />
        </div> */}
      </CardBody>
    </Card>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div className="customer-modern-info-row">
    <i className={icon} />
    <span>{label}</span>
    <strong>{value || "--"}</strong>
  </div>
);

const getActivityKey = (item, index) =>
  `${item.bill_id || "activity"}-${item.date || ""}-${item.time || ""}-${index}`;

const ActivityEmpty = ({
  loading,
  text = "Chưa có hoạt động",
  loadingText = "Đang tải hoạt động...",
  onRefreshActivity,
}) => (
  <div className="customer-modern-activity-empty">
    {loading ? loadingText : text}
    {!loading && onRefreshActivity ? (
      <button type="button" onClick={onRefreshActivity}>
        Làm mới
      </button>
    ) : null}
  </div>
);

const OptionBadgeRow = ({ badges, compact }) => {
  const items = normalizeOptionBadges(badges);
  if (!items.length) return null;

  return (
    <div className={classnames("customer-modern-option-badge-row", { compact })}>
      {items.map((badge, index) => (
        <span
          key={`${badge.type || "option"}-${badge.value || badge.display_label || index}-${index}`}
          className={classnames(
            "customer-modern-option-badge",
            `tone-${badge.type || "default"}`,
          )}
        >
          {getOptionBadgeText(badge)}
        </span>
      ))}
    </div>
  );
};

const OrderProductSummary = ({ products }) => {
  const items = safeArray(products);
  if (!items.length) return null;

  const visibleItems = items.slice(0, 10);
  const hiddenCount = Math.max(0, items.length - visibleItems.length);

  return (
    <div className="customer-modern-order-products">
      {visibleItems.map((product, index) => {
        const optionBadges = getProductOptionBadges(product);

        return (
          <span
            key={`${product.product_id || "product"}-${index}`}
            className="customer-modern-order-product-pill"
          >
            <strong>
              {product.name || "Sản phẩm"} x{formatCount(product.count)}
            </strong>
            <OptionBadgeRow badges={optionBadges} compact />
          </span>
        );
      })}
      {hiddenCount ? <em>+{formatCount(hiddenCount)} sản phẩm khác</em> : null}
    </div>
  );
};

const OrderActivityList = ({ activity, loading, onRefreshActivity }) => {
  const orders = safeArray(activity);
  if (loading) return <ActivityEmpty loading />;
  if (!orders.length) {
    return <ActivityEmpty text="Chưa có đơn hàng" onRefreshActivity={onRefreshActivity} />;
  }

  return (
    <div className="customer-modern-order-list">
      {orders.map((item, index) => {
        const debtDetails = getActivityDebtDetails(item);
        const { orderAmount, debtAmount, paidAmount, isPartial, isDebt } = debtDetails;

        return (
          <div
            key={getActivityKey(item, index)}
            className={classnames("customer-modern-order-card", {
              debt: isDebt && !isPartial,
              partial: isPartial,
            })}
          >
            <div className="customer-modern-order-card-head">
              <div>
                <small>{formatDateTime(item.date, item.time)}</small>
                <div className="d-flex align-items-center">
                  <strong>{getBillShortCode(item.bill_id)}</strong>
                  {isPartial ? (
                    <span className="customer-modern-order-badge-partial ml-2">
                      Thanh toán 1 phần
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="text-right">
                <strong
                  className={classnames("customer-modern-order-amount", {
                    debt: isDebt && !isPartial,
                    partial: isPartial,
                  })}
                >
                  {formatMoney(orderAmount)}
                </strong>
                {isPartial && debtAmount > 0 ? (
                  <small className="text-danger font-weight-bold mt-1" style={{ fontSize: "11px" }}>
                    Còn nợ: {formatMoney(debtAmount)}
                  </small>
                ) : null}
              </div>
            </div>
            <div className="customer-modern-order-meta">
              <span>
                <i className="simple-icon-basket" />
                {formatCount(item.item_count)} sản phẩm
              </span>
              <span>
                <i className="simple-icon-credit-card" />
                {formatPaymentMethods(item.payment_methods)}
              </span>
              <span
                className={classnames({
                  "text-warning font-weight-bold": isPartial,
                  "text-danger": isDebt && !isPartial,
                })}
              >
                <i className={isPartial ? "simple-icon-clock" : "simple-icon-check"} />
                {isPartial ? "Thanh toán 1 phần" : item.status_label || "--"}
              </span>
            </div>
            <OrderProductSummary products={item.products} />
            {isPartial ? (
              <div className="customer-modern-order-debt-note partial">
                Đã thanh toán: <strong>{formatMoney(paidAmount)}</strong> · Còn nợ:{" "}
                <strong className="text-danger">{formatMoney(debtAmount)}</strong>
              </div>
            ) : isDebt ? (
              <div className="customer-modern-order-debt-note">
                Công nợ phát sinh: {formatMoney(debtAmount || orderAmount)}
                {safeNumber(paidAmount) > 0
                  ? ` · Đã thanh toán: ${formatMoney(paidAmount)}`
                  : ""}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const ProductUsageList = ({
  products,
  customer,
  loading,
  onRefreshProducts,
  onExportCustomerMatrix,
}) => {
  const items = safeArray(products);
  if (loading) {
    return <ActivityEmpty loading loadingText="Đang tải sản phẩm..." />;
  }
  if (!items.length) {
    return (
      <ActivityEmpty
        text="Chưa có sản phẩm đã mua"
        onRefreshActivity={onRefreshProducts}
      />
    );
  }

  return (
    <div className="customer-modern-product-usage-list">
      {onExportCustomerMatrix && customer ? (
        <div className="d-flex justify-content-between align-items-center mb-3 px-1">
          <small className="text-muted font-weight-bold">
            {items.length} mặt hàng đã mua
          </small>
          <Button
            color="primary"
            outline
            size="xs"
            className="py-1 px-2"
            onClick={() => onExportCustomerMatrix(customer)}
          >
            <i className="simple-icon-cloud-download mr-1" />
            Xuất sổ giao hàng (Excel)
          </Button>
        </div>
      ) : null}
      {items.map((item, index) => {
        const quantityLabel = `${formatCount(item.quantity)} ${item.sale_unit || "sản phẩm"}`;
        const optionBadges = getProductOptionBadges(item);

        return (
          <div
            key={`${item.product_id || "product"}-${index}`}
            className="customer-modern-product-usage-card"
          >
            <span className="customer-modern-product-usage-icon">
              <i className="simple-icon-basket" />
            </span>
            <div>
              <div className="customer-modern-product-usage-head">
                <strong>{item.name || "Sản phẩm"}</strong>
                <span>{formatMoney(item.amount)}</span>
              </div>
              <div className="customer-modern-product-usage-meta">
                <span>
                  <i className="simple-icon-layers" />
                  {quantityLabel}
                </span>
                <span>
                  <i className="simple-icon-docs" />
                  {formatCount(item.order_count)} đơn
                </span>
                <span>
                  <i className="simple-icon-clock" />
                  {formatDateTime(item.last_order_date, item.last_order_time)}
                </span>
              </div>
              <OptionBadgeRow badges={optionBadges} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DebtActivityList = ({ activity, customer, loading, onRefreshActivity, isDateFiltered }) => {
  const debts = safeArray(activity);
  const currentDebt = safeNumber(customer && customer.debt);

  return (
    <div className="customer-modern-debt-pane">
      <div className="customer-modern-debt-summary-card">
        <span>{isDateFiltered ? "Công nợ phát sinh trong kỳ" : "Dư nợ hiện tại"}</span>
        <strong>{formatMoney(currentDebt)}</strong>
        {isDateFiltered && customer?.all_time_debt !== undefined && (
          <div className="text-muted mt-1" style={{ fontSize: "12px" }}>
            Tổng nợ tích lũy toàn thời gian: <strong className="text-dark">{formatMoney(customer.all_time_debt)}</strong>
          </div>
        )}
        <small>
          {debts.length
            ? `${formatCount(debts.length)} đơn hàng có phát sinh công nợ trong khoảng này`
            : "Chưa có đơn ghi nợ trong khoảng này"}
        </small>
      </div>

      {loading ? (
        <ActivityEmpty loading />
      ) : debts.length ? (
        <div className="customer-modern-order-list">
          {debts.map((item, index) => {
            const debtDetails = getActivityDebtDetails(item);
            const { orderAmount, debtAmount, paidAmount, isPartial } = debtDetails;

            return (
              <div
                key={getActivityKey(item, index)}
                className={classnames("customer-modern-order-card debt", {
                  partial: isPartial,
                })}
              >
                <div className="customer-modern-order-card-head">
                  <div>
                    <small>{formatDateTime(item.date, item.time)}</small>
                    <div className="d-flex align-items-center">
                      <strong>{getBillShortCode(item.bill_id)}</strong>
                      {isPartial ? (
                        <span className="customer-modern-order-badge-partial ml-2">
                          Nợ 1 phần
                        </span>
                      ) : (
                        <span
                          className="badge badge-danger text-white ml-2"
                          style={{ fontSize: "10px" }}
                        >
                          Nợ toàn bộ
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <strong className="customer-modern-order-amount debt">
                      {formatMoney(debtAmount || orderAmount)}
                    </strong>
                    {isPartial && orderAmount > debtAmount ? (
                      <small className="text-muted mt-1" style={{ fontSize: "11px" }}>
                        Tổng đơn: {formatMoney(orderAmount)}
                      </small>
                    ) : null}
                  </div>
                </div>
                <div className="customer-modern-order-meta">
                  <span>
                    <i className="simple-icon-basket" />
                    {formatCount(item.item_count)} sản phẩm
                  </span>
                  <span>
                    <i className="simple-icon-credit-card" />
                    {formatPaymentMethods(item.payment_methods)}
                  </span>
                  <span className={isPartial ? "text-warning font-weight-bold" : "text-danger"}>
                    <i className={isPartial ? "simple-icon-clock" : "simple-icon-exclamation"} />
                    {isPartial ? "Thanh toán 1 phần" : "Ghi nợ"}
                  </span>
                </div>
                <OrderProductSummary products={item.products} />
                <div className="customer-modern-order-debt-note">
                  {isPartial ? (
                    <>
                      Còn nợ: <strong className="text-danger">{formatMoney(debtAmount)}</strong> · Đã thanh toán: <strong>{formatMoney(paidAmount)}</strong> (Tổng đơn: {formatMoney(orderAmount)})
                    </>
                  ) : (
                    <>
                      Ghi nợ toàn bộ hóa đơn: <strong className="text-danger">{formatMoney(debtAmount || orderAmount)}</strong>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <ActivityEmpty text="Chưa có lịch sử công nợ" onRefreshActivity={onRefreshActivity} />
      )}
    </div>
  );
};

const ActivityList = ({ activity, loading, onRefreshActivity }) => {
  const items = safeArray(activity);
  if (loading) return <ActivityEmpty loading />;
  if (!items.length) {
    return <ActivityEmpty text="Chưa có hoạt động" onRefreshActivity={onRefreshActivity} />;
  }

  return (
    <div className="customer-modern-activity-list">
      {items.map((item, index) => {
        const debtDetails = getActivityDebtDetails(item);
        const { orderAmount, debtAmount, paidAmount, isPartial, isDebt } = debtDetails;
        const title = isPartial
          ? "Đơn hàng thanh toán 1 phần"
          : isDebt
            ? "Đơn hàng ghi nợ"
            : item.title || "Đơn hàng đã thanh toán";

        return (
          <div key={getActivityKey(item, index)} className="customer-modern-activity-item">
            <span className={classnames("customer-modern-activity-icon", getActivityTone(item))}>
              <i className={getActivityIcon(item)} />
            </span>
            <div>
              <small>
                {formatDateTime(item.date, item.time)}
              </small>
              <strong>
                {title}
                {item.bill_id ? (
                  <>
                    {" "}
                    <span>{getBillShortCode(item.bill_id)}</span>
                  </>
                ) : null}
                {" - "}
                {formatMoney(orderAmount)}
              </strong>
              <em>{item.description}</em>
              <div className="customer-modern-activity-meta">
                <span>{formatPaymentMethods(item.payment_methods)}</span>
                <span className={classnames({ "text-warning font-weight-bold": isPartial, "text-danger": isDebt && !isPartial })}>
                  {isPartial ? "Thanh toán 1 phần" : isDebt ? "Ghi nợ" : item.status_label || "--"}
                </span>
                {isPartial ? (
                  <span>
                    Đã trả {formatMoney(paidAmount)} · Còn nợ <strong className="text-danger">{formatMoney(debtAmount)}</strong>
                  </span>
                ) : isDebt ? (
                  <span>Công nợ {formatMoney(debtAmount || orderAmount)}</span>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default memo(CustomerModernDashboard);
